import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  Heading,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Input,
  Select,
  HStack,
  VStack,
  Spinner,
  Center,
  Text,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  Textarea,
  Checkbox,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Stat,
  StatLabel,
  StatNumber,
  Grid,
  GridItem,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Card,
  CardBody,
  CardHeader,
  Divider,
  Progress,
  List,
  ListItem,
  ListIcon,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  SimpleGrid,
  StatHelpText,
  StatArrow,
} from '@chakra-ui/react';
import { FiEdit2, FiEye, FiUsers, FiPlus, FiTrendingUp, FiDollarSign, FiCalendar, FiMapPin, FiUser, FiCheckCircle, FiXCircle, FiClock, FiTrash2 } from 'react-icons/fi';
import { Timestamp, deleteField } from 'firebase/firestore';
import { 
  getAllBookingsWithCustomer, 
  BookingWithCustomer, 
  getAllUsers, 
  User, 
  updateBooking, 
  createBooking,
  getAllBookingPools,
  BookingPool,
  getPoolAnalytics,
  getPoolPerformanceMetrics,
  getBookingsByPool,
  createBookingPool,
  updateBookingPool,
  deleteBookingPool,
  addParticipantToPool,
  removeParticipantFromPool,
  getBookingPoolMembers,
  getPaymentsByBookingIds,
  getAllServiceTypes,
  ServiceType,
  getAllGiftCards,
  getGiftCardAnalytics,
  GiftCodeWithCustomer,
  getActiveCustomers,
  getCustomerAddresses,
  getCustomerVehicles,
  SavedAddress,
  CustomerVehicle,
} from '../services/firestore';
import ExportModal from './Exports/ExportModal';
import BookingsCalendar from './BookingsManagement/BookingsCalendar';
import { getCoordinatesForAddress } from '../services/geocoding';

// Cast icons as React.ElementType to fix TypeScript issues
const FiEdit2Icon = FiEdit2 as React.ElementType;
const FiEyeIcon = FiEye as React.ElementType;
const FiUsersIcon = FiUsers as React.ElementType;
const FiPlusIcon = FiPlus as React.ElementType;
const FiTrendingUpIcon = FiTrendingUp as React.ElementType;
const FiDollarSignIcon = FiDollarSign as React.ElementType;
const FiCalendarIcon = FiCalendar as React.ElementType;
const FiMapPinIcon = FiMapPin as React.ElementType;
const FiUserIcon = FiUser as React.ElementType;
const FiCheckCircleIcon = FiCheckCircle as React.ElementType;
const FiXCircleIcon = FiXCircle as React.ElementType;
const FiClockIcon = FiClock as React.ElementType;
const FiTrash2Icon = FiTrash2 as React.ElementType;

// Helper to format cents to dollars
const formatAmount = (amount: number | undefined) => `$${((amount ?? 0) / 100).toFixed(2)}`;

const BookingsManagement: React.FC = () => {
  const [bookings, setBookings] = useState<BookingWithCustomer[]>([]);
  const [filteredBookings, setFilteredBookings] = useState<BookingWithCustomer[]>([]);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [pools, setPools] = useState<BookingPool[]>([]);
  const [poolAnalytics, setPoolAnalytics] = useState<any>(null);
  const [giftCards, setGiftCards] = useState<GiftCodeWithCustomer[]>([]);
  const [filteredGiftCards, setFilteredGiftCards] = useState<GiftCodeWithCustomer[]>([]);
  const [giftCardAnalytics, setGiftCardAnalytics] = useState<any>(null);
  const [giftCardStatusFilter, setGiftCardStatusFilter] = useState('all');
  const [giftCardSearchTerm, setGiftCardSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [poolFilter, setPoolFilter] = useState('all');
  const [selectedBookings, setSelectedBookings] = useState<string[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<BookingWithCustomer | null>(null);
  const [selectedPool, setSelectedPool] = useState<BookingPool | null>(null);
  const [editingStatus, setEditingStatus] = useState('');
  const [editingTechnician, setEditingTechnician] = useState('');
  const [editingNotes, setEditingNotes] = useState('');
  const [updating, setUpdating] = useState(false);
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkStatus, setBulkStatus] = useState('');
  const [bulkTechnician, setBulkTechnician] = useState('');
  
  // Create booking form state
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  // Service selection for multi-service support
  interface FormServiceSelection {
    serviceTypeId: string;
    variantId: string;
    addOns: string[];
    vehicleCount: number;
    quantity: number;
  }

  const [createForm, setCreateForm] = useState({
    customerId: '',
    customerName: '',
    serviceType: '', // Legacy single service (for backward compatibility)
    variantId: '',
    addOns: [] as string[],
    services: [] as FormServiceSelection[], // New: multiple services
    scheduledDate: '',
    scheduledTime: '',
    amount: '',
    address: '' as string | SavedAddress, // Can be string or SavedAddress object
    selectedAddress: null as SavedAddress | null, // Store selected address object
    notes: '',
    technicianId: '',
    status: 'pending' as const,
    poolId: '',
    isPoolBooking: false,
    // Vehicle fields
    vehicleId: '',
    vehicleInfo: {
      make: '',
      model: '',
      year: '',
      color: '',
      licensePlate: '',
    },
    vehicles: [] as Array<{ // New: multiple vehicles
      vehicleId?: string;
      vehicleInfo: {
        make: string;
        model: string;
        year: string;
        color: string;
        licensePlate: string;
      };
    }>,
  });
  
  // Pool management state
  const [isPoolDrawerOpen, setIsPoolDrawerOpen] = useState(false);
  const [isCreatePoolOpen, setIsCreatePoolOpen] = useState(false);
  const [creatingPool, setCreatingPool] = useState(false);
  const [createPoolForm, setCreatePoolForm] = useState({
    name: '',
    description: '',
    label: '',
    street: '',
    city: '',
    state: '',
    zipCode: '',
    latitude: '',
    longitude: '',
    startTime: '',
    type: '',
  });
  
  const [poolBookings, setPoolBookings] = useState<BookingWithCustomer[]>([]);
  const [poolParticipants, setPoolParticipants] = useState<any[]>([]);
  const [poolRevenue, setPoolRevenue] = useState(0);
  const [loadingPoolDetails, setLoadingPoolDetails] = useState(false);
  
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { isOpen: isBulkOpen, onOpen: onBulkOpen, onClose: onBulkClose } = useDisclosure();
  const toast = useToast();

  const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
  const [isExportOpen, setIsExportOpen] = useState(false);

  const [isBookingDrawerOpen, setIsBookingDrawerOpen] = useState(false);
  const [viewingBooking, setViewingBooking] = useState<BookingWithCustomer | null>(null);

  // Customer search and autofill state
  const [customers, setCustomers] = useState<User[]>([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState<User | null>(null);
  const [customerAddresses, setCustomerAddresses] = useState<SavedAddress[]>([]);
  const [customerVehicles, setCustomerVehicles] = useState<CustomerVehicle[]>([]);
  const [loadingCustomerData, setLoadingCustomerData] = useState(false);

  // Add state for editing booking details
  const [editingBookingDetails, setEditingBookingDetails] = useState<BookingWithCustomer | null>(null);
  const [isEditingBooking, setIsEditingBooking] = useState(false);
  const [savingBooking, setSavingBooking] = useState(false);

  // Add form state for booking details editing
  const [bookingEditForm, setBookingEditForm] = useState({
    customerName: '',
    serviceType: '', // Legacy single service
    variantId: '',
    addOns: [] as string[],
    services: [] as FormServiceSelection[], // New: multiple services
    scheduledDate: '',
    scheduledTime: '',
    amount: '',
    address: '',
    notes: '',
    technicianId: '',
    status: 'pending' as 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled',
    poolId: '',
    isPoolBooking: false,
    paymentStatus: '' as '' | 'pending' | 'partial' | 'deposit_paid' | 'paid' | 'refunded' | 'failed',
    amountPaid: '',
    remainingAmount: '',
    // Vehicle fields
    vehicleId: '',
    vehicleInfo: {
      make: '',
      model: '',
      year: '',
      color: '',
      licensePlate: '',
    },
    vehicles: [] as Array<{
      vehicleId?: string;
      vehicleInfo: {
        make: string;
        model: string;
        year: string;
        color: string;
        licensePlate: string;
      };
    }>,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    fetchData();
    // Fetch service types for mapping
    getAllServiceTypes().then(setServiceTypes);
    // Load active customers for booking form
    loadCustomers();
  }, []);

  // Load active customers
  const loadCustomers = async () => {
    try {
      // Try to get active customers, fallback to filtering all users
      try {
        const activeCustomers = await getActiveCustomers();
        setCustomers(activeCustomers);
      } catch (error) {
        // If composite index doesn't exist, filter in memory
        console.warn('Could not query active customers directly, filtering in memory:', error);
        const allUsers = await getAllUsers();
        const activeCustomers = allUsers.filter(
          user => user.role === 'customer' && user.isActive !== false
        );
        setCustomers(activeCustomers);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
      toast({
        title: 'Error loading customers',
        description: 'Failed to fetch customer list',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  // Handle customer selection
  const handleCustomerSelect = async (customer: User | null) => {
    setSelectedCustomer(customer);
    setCustomerAddresses([]);
    setCustomerVehicles([]);
    
    if (!customer) {
      // Clear form when customer is deselected
      setCreateForm(prev => ({
        ...prev,
        customerId: '',
        customerName: '',
        address: '',
        selectedAddress: null,
        vehicleId: '',
        vehicleInfo: {
          make: '',
          model: '',
          year: '',
          color: '',
          licensePlate: '',
        },
        vehicles: [],
      }));
      return;
    }

    // Update form with customer info
    const customerName = `${customer.firstName || ''} ${customer.lastName || ''}`.trim() || customer.email;
    setCreateForm(prev => ({
      ...prev,
      customerId: customer.id,
      customerName,
    }));

    // Load customer addresses and vehicles
    setLoadingCustomerData(true);
    try {
      const [addresses, vehicles] = await Promise.all([
        getCustomerAddresses(customer.id),
        getCustomerVehicles(customer.id),
      ]);
      
      setCustomerAddresses(addresses);
      setCustomerVehicles(vehicles);
      
      // Auto-select first address if available
      if (addresses.length > 0) {
        const firstAddress = addresses[0];
        const addressString = firstAddress.street || firstAddress.line1 
          ? `${firstAddress.street || firstAddress.line1 || ''}, ${firstAddress.city || ''}, ${firstAddress.state || ''} ${firstAddress.zipCode || ''}`.trim()
          : '';
        setCreateForm(prev => ({
          ...prev,
          address: addressString,
          selectedAddress: firstAddress,
        }));
      }
      
      // Auto-select first vehicle if available
      if (vehicles.length > 0) {
        const firstVehicle = vehicles[0];
        setCreateForm(prev => ({
          ...prev,
          vehicleId: firstVehicle.id,
          vehicleInfo: {
            make: firstVehicle.make || '',
            model: firstVehicle.model || '',
            year: firstVehicle.year?.toString() || '',
            color: firstVehicle.color || '',
            licensePlate: firstVehicle.licensePlate || '',
          },
        }));
      }
    } catch (error) {
      console.error('Error loading customer data:', error);
      toast({
        title: 'Error loading customer data',
        description: 'Failed to load addresses and vehicles',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingCustomerData(false);
    }
  };

  // Filter customers based on search term
  const filteredCustomers = customers.filter(customer => {
    if (!customerSearchTerm) return true;
    const searchLower = customerSearchTerm.toLowerCase();
    const firstName = (customer.firstName || '').toLowerCase();
    const lastName = (customer.lastName || '').toLowerCase();
    const email = (customer.email || '').toLowerCase();
    const fullName = `${firstName} ${lastName}`.trim();
    
    return fullName.includes(searchLower) || 
           email.includes(searchLower) ||
           firstName.includes(searchLower) ||
           lastName.includes(searchLower);
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    filterBookings();
  }, [bookings, searchTerm, statusFilter, poolFilter]);

  // Filter gift cards
  useEffect(() => {
    let filtered = giftCards;

    // Search filter
    if (giftCardSearchTerm) {
      filtered = filtered.filter(gc => {
        const code = gc.code || '';
        const recipientEmail = gc.recipientEmail || '';
        const recipientName = gc.recipientName || '';
        const purchaserName = gc.purchaser ? `${gc.purchaser.firstName || ''} ${gc.purchaser.lastName || ''}`.trim() : '';
        const purchaserEmail = gc.purchaser?.email || '';
        
        return code.toLowerCase().includes(giftCardSearchTerm.toLowerCase()) ||
               recipientEmail.toLowerCase().includes(giftCardSearchTerm.toLowerCase()) ||
               recipientName.toLowerCase().includes(giftCardSearchTerm.toLowerCase()) ||
               purchaserName.toLowerCase().includes(giftCardSearchTerm.toLowerCase()) ||
               purchaserEmail.toLowerCase().includes(giftCardSearchTerm.toLowerCase());
      });
    }

    // Status filter
    if (giftCardStatusFilter !== 'all') {
      filtered = filtered.filter(gc => gc.status === giftCardStatusFilter);
    }

    setFilteredGiftCards(filtered);
  }, [giftCards, giftCardSearchTerm, giftCardStatusFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [bookingsData, techniciansData, poolsData, analyticsData, giftCardsData, giftCardAnalyticsData] = await Promise.all([
        getAllBookingsWithCustomer(),
        getAllUsers(),
        getAllBookingPools(),
        getPoolAnalytics(),
        getAllGiftCards(),
        getGiftCardAnalytics(),
      ]);
      
      setBookings(bookingsData);
      setTechnicians(techniciansData.filter(user => user.role === 'technician'));
      setPools(poolsData);
      setPoolAnalytics(analyticsData);
      setGiftCards(giftCardsData);
      setFilteredGiftCards(giftCardsData);
      setGiftCardAnalytics(giftCardAnalyticsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast({
        title: 'Error loading data',
        description: 'Failed to fetch bookings and pools data',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const filterBookings = () => {
    let filtered = bookings;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(booking => {
        const customerName = getCustomerName(booking);
        const serviceType = booking.serviceType || '';
        const bookingId = booking.id || '';
        const poolName = booking.poolName || '';
        
        return customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
               serviceType.toLowerCase().includes(searchTerm.toLowerCase()) ||
               bookingId.toLowerCase().includes(searchTerm.toLowerCase()) ||
               poolName.toLowerCase().includes(searchTerm.toLowerCase());
      });
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter(booking => booking.status === statusFilter);
    }

    // Pool filter
    if (poolFilter === 'pool') {
      filtered = filtered.filter(booking => booking.isPoolBooking);
    } else if (poolFilter === 'individual') {
      filtered = filtered.filter(booking => !booking.isPoolBooking);
    } else if (poolFilter !== 'all') {
      filtered = filtered.filter(booking => booking.poolId === poolFilter);
    }

    setFilteredBookings(filtered);
  };

  const getCustomerName = (booking: BookingWithCustomer) => {
    if (booking.customer?.firstName && booking.customer?.lastName) {
      return `${booking.customer.firstName} ${booking.customer.lastName}`;
    } else if (booking.customer?.firstName) {
      return booking.customer.firstName;
    } else if (booking.customer?.lastName) {
      return booking.customer.lastName;
    } else if (booking.customer?.email) {
      return booking.customer.email;
    } else {
      return 'Unknown Customer';
    }
  };

  const handleEditBooking = (booking: BookingWithCustomer) => {
    setSelectedBooking(booking);
    setEditingStatus(booking.status);
    setEditingNotes(booking.notes || '');
    onOpen();
  };

  const handleViewPool = async (pool: BookingPool) => {
    setSelectedPool(pool);
    setIsPoolDrawerOpen(true);
    setLoadingPoolDetails(true);
    // Fetch bookings for this pool
    const bookings = await getBookingsByPool(pool.id);
    setPoolBookings(bookings);
    // Fetch participants for this pool
    const participants = await getBookingPoolMembers(pool.id);
    setPoolParticipants(participants);
    // Fetch payments for these bookings and sum revenue
    const bookingIds = bookings.map(b => b.id);
    const payments = await getPaymentsByBookingIds(bookingIds);
    const revenue = payments
      .filter((p: any) => p.status === 'completed')
      .reduce((sum: number, p: any) => sum + p.amount, 0);
    setPoolRevenue(revenue);
    setLoadingPoolDetails(false);
  };

  const handleCreatePool = async () => {
    try {
      setCreatingPool(true);
      const startTime = Timestamp.fromDate(new Date(createPoolForm.startTime));
      const poolData = {
        name: createPoolForm.name,
        description: createPoolForm.description,
        label: createPoolForm.label,
        street: createPoolForm.street,
        city: createPoolForm.city,
        state: createPoolForm.state,
        zipCode: createPoolForm.zipCode,
        latitude: parseFloat(createPoolForm.latitude),
        longitude: parseFloat(createPoolForm.longitude),
        startTime,
        type: createPoolForm.type,
        status: 'active',
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
        customerId: '', // Set as needed
        poolAddress: {
          street: createPoolForm.street,
          city: createPoolForm.city,
          state: createPoolForm.state,
          zipCode: createPoolForm.zipCode,
        },
      };
      await createBookingPool(poolData);
      toast({
        title: 'Pool created',
        description: 'Booking pool has been created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      setIsCreatePoolOpen(false);
      setCreatePoolForm({
        name: '',
        description: '',
        label: '',
        street: '',
        city: '',
        state: '',
        zipCode: '',
        latitude: '',
        longitude: '',
        startTime: '',
        type: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error creating pool:', error);
      toast({
        title: 'Error creating pool',
        description: 'Failed to create booking pool',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreatingPool(false);
    }
  };

  const handleUpdateBooking = async () => {
    if (!selectedBooking) return;

    try {
      setUpdating(true);
      
      const updates: Partial<BookingWithCustomer> = {
        status: editingStatus as any,
        notes: editingNotes,
      };

      if (editingTechnician) {
        updates.technicianId = editingTechnician;
      }

      await updateBooking(selectedBooking.id, updates);
      
      // Update local state
      setBookings(bookings.map(booking => 
        booking.id === selectedBooking.id 
          ? { ...booking, ...updates }
          : booking
      ));

      toast({
        title: 'Booking updated',
        description: 'Booking has been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onClose();
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: 'Error updating booking',
        description: 'Failed to update booking',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setUpdating(false);
    }
  };

  const handleBulkUpdate = async () => {
    if (selectedBookings.length === 0) return;

    try {
      setBulkUpdating(true);
      
      const updates: Partial<BookingWithCustomer> = {};
      
      if (bulkStatus) {
        updates.status = bulkStatus as any;
      }
      
      if (bulkTechnician) {
        updates.technicianId = bulkTechnician;
      }

      // Update all selected bookings
      await Promise.all(
        selectedBookings.map(bookingId => updateBooking(bookingId, updates))
      );

      // Update local state
      setBookings(bookings.map(booking => 
        selectedBookings.includes(booking.id)
          ? { ...booking, ...updates }
          : booking
      ));

      setSelectedBookings([]);
      
      toast({
        title: 'Bulk update completed',
        description: `${selectedBookings.length} bookings have been updated`,
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      onBulkClose();
    } catch (error) {
      console.error('Error bulk updating bookings:', error);
      toast({
        title: 'Error bulk updating',
        description: 'Failed to update selected bookings',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setBulkUpdating(false);
    }
  };

  const handleSelectBooking = (bookingId: string, checked: boolean) => {
    if (checked) {
      setSelectedBookings([...selectedBookings, bookingId]);
    } else {
      setSelectedBookings(selectedBookings.filter(id => id !== bookingId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedBookings(filteredBookings.map(booking => booking.id));
    } else {
      setSelectedBookings([]);
    }
  };

  const handleCreateBooking = async () => {
    try {
      setCreating(true);
      
      // Validate required fields
      if (!createForm.customerName || !createForm.serviceType || !createForm.scheduledDate || !createForm.amount) {
        toast({
          title: 'Validation Error',
          description: 'Please fill in all required fields',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
        setCreating(false);
        return;
      }

      const scheduledDateTime = new Date(`${createForm.scheduledDate}T${createForm.scheduledTime || '09:00'}`);
      
      // Create proper address object structure - use selected address if available, otherwise create from string
      let addressData: any = null;
      let addressId: string | undefined = undefined;
      
      if (createForm.selectedAddress) {
        // Use the selected SavedAddress object directly
        // Store the addressId reference to the addresses collection
        addressId = createForm.selectedAddress.id;
        
        // Create address object matching the structure shown in Firestore
        addressData = {
          street: createForm.selectedAddress.street || createForm.selectedAddress.line1 || '',
          city: createForm.selectedAddress.city || '',
          state: createForm.selectedAddress.state || '',
          zipCode: createForm.selectedAddress.zipCode || '',
          label: createForm.selectedAddress.label || 'Service Address',
        };
        
        // Only include latitude/longitude if they are valid numbers
        if (typeof createForm.selectedAddress.latitude === 'number' && !isNaN(createForm.selectedAddress.latitude)) {
          addressData.latitude = createForm.selectedAddress.latitude;
        }
        if (typeof createForm.selectedAddress.longitude === 'number' && !isNaN(createForm.selectedAddress.longitude)) {
          addressData.longitude = createForm.selectedAddress.longitude;
        }
      } else if (createForm.address && typeof createForm.address === 'string' && createForm.address.trim()) {
        // Manual address entry - geocode to get coordinates
        try {
          const coords = await getCoordinatesForAddress(createForm.address.trim());
          addressData = {
            street: createForm.address,
            city: '',
            state: '',
            zipCode: '',
            label: 'Service Address',
            latitude: coords.latitude,
            longitude: coords.longitude,
          };
          
          toast({
            title: 'Address geocoded',
            description: `Location found: ${coords.latitude.toFixed(4)}, ${coords.longitude.toFixed(4)}`,
            status: 'success',
            duration: 2000,
            isClosable: true,
          });
        } catch (geocodingError) {
          console.error('Geocoding error:', geocodingError);
          // Create address without coordinates - admin can update later
          addressData = {
            street: createForm.address,
            city: '',
            state: '',
            zipCode: '',
            label: 'Service Address',
          };
          
          toast({
            title: 'Geocoding failed',
            description: 'Address saved without coordinates. Please update with valid address for map features.',
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
      }
      
      const bookingData: any = {
        customerId: createForm.customerId || 'admin-created', // Default if not provided
        customerName: createForm.customerName,
        scheduledDate: Timestamp.fromDate(scheduledDateTime),
        scheduledTime: Timestamp.fromDate(scheduledDateTime), // Add scheduledTime
        status: createForm.status,
        amount: parseFloat(createForm.amount) * 100, // Convert to cents
        address: addressData, // Use address object, not string
        isPoolBooking: createForm.isPoolBooking,
      };

      // Conditionally include optional fields - only if they have values (Firestore doesn't allow undefined)
      if (addressId) {
        bookingData.addressId = addressId;
      }

      if (createForm.notes && createForm.notes.trim()) {
        bookingData.notes = createForm.notes;
      }

      if (createForm.technicianId && createForm.technicianId.trim()) {
        bookingData.technicianId = createForm.technicianId;
      }

      if (createForm.isPoolBooking && createForm.poolId && createForm.poolId.trim()) {
        bookingData.poolId = createForm.poolId;
        const selectedPool = pools.find(p => p.id === createForm.poolId);
        if (selectedPool?.name) {
          bookingData.poolName = selectedPool.name;
        }
      }

      // Handle services: prefer services array, fallback to single serviceType
      if (createForm.services && createForm.services.length > 0) {
        // Multi-service booking
        bookingData.services = createForm.services
          .filter(s => s.serviceTypeId) // Only include services with a selected type
          .map(service => ({
            packageId: service.variantId 
              ? `${service.serviceTypeId}:${service.variantId}` 
              : service.serviceTypeId,
            addOns: service.addOns || [],
            vehicleCount: service.vehicleCount || 1,
            quantity: service.quantity || 1,
          }));
        
        // Also set serviceType to first service for backward compatibility
        if (bookingData.services.length > 0) {
          bookingData.serviceType = createForm.services[0].serviceTypeId;
          if (createForm.services[0].variantId) {
            bookingData.variantId = createForm.services[0].variantId;
          }
          if (createForm.services[0].addOns && createForm.services[0].addOns.length > 0) {
            bookingData.addOns = createForm.services[0].addOns;
          }
        }
      } else if (createForm.serviceType) {
        // Legacy single-service booking
        bookingData.serviceType = createForm.serviceType;
        if (createForm.variantId) {
          bookingData.variantId = createForm.variantId;
        }
        if (createForm.addOns && createForm.addOns.length > 0) {
          bookingData.addOns = createForm.addOns;
        }
      }

      // Handle vehicles: prefer vehicles array, fallback to single vehicleInfo
      if (createForm.vehicles && createForm.vehicles.length > 0) {
        // Multi-vehicle booking
        bookingData.vehicles = createForm.vehicles
          .filter(v => v.vehicleInfo.make || v.vehicleInfo.model) // Only include vehicles with at least make or model
          .map(vehicle => {
            const vehicleData: any = {
              vehicleInfo: {
                make: vehicle.vehicleInfo.make || '',
                model: vehicle.vehicleInfo.model || '',
                color: vehicle.vehicleInfo.color || '',
                licensePlate: vehicle.vehicleInfo.licensePlate || '',
              },
            };
            
            // Only include vehicleId if it has a value (not undefined)
            if (vehicle.vehicleId && vehicle.vehicleId.trim()) {
              vehicleData.vehicleId = vehicle.vehicleId;
            }
            
            // Only include year if it's a valid number (not undefined)
            if (vehicle.vehicleInfo.year) {
              const yearValue = parseInt(vehicle.vehicleInfo.year);
              if (!isNaN(yearValue)) {
                vehicleData.vehicleInfo.year = yearValue;
              }
            }
            
            return vehicleData;
          });
        
        // Also set vehicleInfo to first vehicle for backward compatibility
        if (bookingData.vehicles.length > 0) {
          bookingData.vehicleInfo = { ...bookingData.vehicles[0].vehicleInfo };
          if (bookingData.vehicles[0].vehicleId) {
            bookingData.vehicleId = bookingData.vehicles[0].vehicleId;
          }
        }
      } else if (createForm.vehicleInfo.make || createForm.vehicleInfo.model) {
        // Legacy single-vehicle booking
        bookingData.vehicleInfo = {
          make: createForm.vehicleInfo.make || '',
          model: createForm.vehicleInfo.model || '',
          color: createForm.vehicleInfo.color || '',
          licensePlate: createForm.vehicleInfo.licensePlate || '',
        };
        
        // Only include year if it's a valid number (not undefined)
        if (createForm.vehicleInfo.year) {
          const yearValue = parseInt(createForm.vehicleInfo.year);
          if (!isNaN(yearValue)) {
            bookingData.vehicleInfo.year = yearValue;
          }
        }
        
        // Only include vehicleId if it has a value
        if (createForm.vehicleId && createForm.vehicleId.trim()) {
          bookingData.vehicleId = createForm.vehicleId;
        }
      }

      await createBooking(bookingData);
      
      toast({
        title: 'Booking created',
        description: 'Booking has been created successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsCreateOpen(false);
      setCreateForm({
        customerId: '',
        customerName: '',
        serviceType: '',
        variantId: '',
        addOns: [],
        services: [],
        scheduledDate: '',
        scheduledTime: '',
        amount: '',
        address: '',
        selectedAddress: null,
        notes: '',
        technicianId: '',
        status: 'pending',
        poolId: '',
        isPoolBooking: false,
        vehicleId: '',
        vehicleInfo: {
          make: '',
          model: '',
          year: '',
          color: '',
          licensePlate: '',
        },
        vehicles: [],
      });
      setSelectedCustomer(null);
      setCustomerSearchTerm('');
      setCustomerAddresses([]);
      setCustomerVehicles([]);
      
      fetchData(); // Refresh data
    } catch (error) {
      console.error('Error creating booking:', error);
      toast({
        title: 'Error creating booking',
        description: 'Failed to create booking',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setCreating(false);
    }
  };

  const handleCreateFormChange = (field: string, value: string) => {
    setCreateForm(prev => ({ ...prev, [field]: value }));
  };

  const getStatusColor = (status: string | undefined) => {
    switch (status) {
      case 'pending': return 'yellow';
      case 'confirmed': return 'blue';
      case 'in_progress': return 'orange';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  const getPoolStatusColor = (status: BookingPool['status']) => {
    switch (status) {
      case 'forming': return 'yellow';
      case 'confirmed': return 'blue';
      case 'in_progress': return 'orange';
      case 'completed': return 'green';
      case 'cancelled': return 'red';
      default: return 'gray';
    }
  };

  const formatDate = (timestamp: any) => {
    if (!timestamp) return 'N/A';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const getTechnicianName = (technicianId: string) => {
    const technician = technicians.find(t => t.id === technicianId);
    return technician ? `${technician.firstName} ${technician.lastName}` : 'Unassigned';
  };

  const getStatusStats = () => {
    const stats = {
      pending: 0,
      confirmed: 0,
      in_progress: 0,
      completed: 0,
      cancelled: 0,
      total: bookings.length,
      poolBookings: bookings.filter(b => b.poolId).length,
      individualBookings: bookings.filter(b => !b.poolId).length,
    };

    bookings.forEach(booking => {
      if (booking.status) {
        stats[booking.status as keyof typeof stats]++;
      }
    });

    return stats;
  };

  const stats = getStatusStats();

  const getServiceTypeName = (serviceTypeId: string) => {
    const serviceType = serviceTypes.find(st => st.id === serviceTypeId);
    return serviceType ? serviceType.name : serviceTypeId;
  };

  // Helper to get full service details (name, variant, add-ons)
  const getServiceDetails = (booking: BookingWithCustomer) => {
    if (!booking.serviceType) {
      return {
        displayName: 'N/A',
        addOnNames: [],
      };
    }

    const serviceType = serviceTypes.find(st => st.id === booking.serviceType);
    if (!serviceType) {
      return {
        displayName: booking.serviceType,
        addOnNames: booking.addOns || [],
      };
    }

    // Resolve variant name
    let variantName: string | undefined;
    if (booking.variantId && serviceType.variants) {
      const variant = serviceType.variants.find(
        (v) => v.id === booking.variantId && v.isActive !== false
      );
      variantName = variant?.name;
    }

    // Build display name
    const displayName = variantName
      ? `${serviceType.name} - ${variantName}`
      : serviceType.name;

    // Resolve add-on names
    const addOnNames: string[] = [];
    if (booking.addOns && serviceType.extras) {
      booking.addOns.forEach((addOnId: string) => {
        const extra = serviceType.extras.find(
          (e) => e.id === addOnId && e.isActive !== false
        );
        if (extra?.name) {
          addOnNames.push(extra.name);
        } else {
          addOnNames.push(addOnId); // Fallback to ID
        }
      });
    }

    return {
      displayName,
      addOnNames,
    };
  };

  const totalPools = pools.length;
  const activePools = pools.filter(p => p.status === 'active').length;
  const completedPools = pools.filter(p => p.status === 'completed').length;
  const totalPoolRevenue = bookings
    .filter(b => b.poolId)
    .reduce((sum, b) => sum + (typeof b.amount === 'number' && !isNaN(b.amount) ? b.amount : 0), 0);

  // Add a delete handler for pools
  const handleDeletePool = async (poolId: string) => {
    if (!window.confirm('Are you sure you want to delete this pool? This action cannot be undone.')) return;
    try {
      await deleteBookingPool(poolId);
      setPools(pools.filter(p => p.id !== poolId));
      toast({
        title: 'Pool deleted',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Error deleting pool',
        description: error instanceof Error ? error.message : 'Failed to delete pool',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleViewBooking = (booking: BookingWithCustomer) => {
    setViewingBooking(booking);
    setEditingBookingDetails(booking);
    // Initialize the edit form with current booking data
    const scheduledDate = booking.scheduledTime || booking.scheduledDate;
    let date: Date;
    if (scheduledDate && typeof scheduledDate === 'object' && 'toDate' in scheduledDate) {
      date = scheduledDate.toDate();
    } else {
      date = new Date(scheduledDate);
    }
    // Handle address - preserve object structure if it exists, convert to display string
    let addressDisplay = '';
    if (typeof booking.address === 'object' && booking.address !== null) {
      const addr = booking.address as any;
      // Format address for display: "street, city, state zip"
      const street = addr.street || addr.line1 || '';
      const city = addr.city || '';
      const state = addr.state || '';
      const zip = addr.zipCode || '';
      addressDisplay = [street, city, state, zip].filter(Boolean).join(', ');
    } else if (typeof booking.address === 'string') {
      addressDisplay = booking.address;
    }
    
    // Convert services array to form format if it exists
    let services: FormServiceSelection[] = [];
    if (booking.services && Array.isArray(booking.services) && booking.services.length > 0) {
      services = booking.services.map((s: any) => {
        const [serviceTypeId, variantId] = s.packageId?.includes(':') 
          ? s.packageId.split(':') 
          : [s.packageId || '', ''];
        return {
          serviceTypeId,
          variantId: variantId || '',
          addOns: s.addOns || [],
          vehicleCount: s.vehicleCount || 1,
          quantity: s.quantity || 1,
        };
      });
    }

    // Convert vehicles array to form format if it exists
    let vehicles: Array<{
      vehicleId?: string;
      vehicleInfo: {
        make: string;
        model: string;
        year: string;
        color: string;
        licensePlate: string;
      };
    }> = [];
    if (booking.vehicles && Array.isArray(booking.vehicles) && booking.vehicles.length > 0) {
      vehicles = booking.vehicles.map((v: any) => ({
        vehicleId: v.vehicleId || '',
        vehicleInfo: {
          make: v.vehicleInfo?.make || '',
          model: v.vehicleInfo?.model || '',
          year: v.vehicleInfo?.year?.toString() || '',
          color: v.vehicleInfo?.color || '',
          licensePlate: v.vehicleInfo?.licensePlate || '',
        },
      }));
    } else if (booking.vehicleInfo) {
      // Convert legacy single vehicle to array format
      vehicles = [{
        vehicleId: (booking as any).vehicleId || '',
        vehicleInfo: {
          make: booking.vehicleInfo.make || '',
          model: booking.vehicleInfo.model || '',
          year: booking.vehicleInfo.year?.toString() || '',
          color: booking.vehicleInfo.color || '',
          licensePlate: booking.vehicleInfo.licensePlate || '',
        },
      }];
    }
    
    setBookingEditForm({
      customerName: booking.customerName || '',
      serviceType: booking.serviceType || '',
      variantId: booking.variantId || '',
      addOns: booking.addOns || [],
      services,
      scheduledDate: date.toISOString().split('T')[0],
      scheduledTime: date.toTimeString().slice(0, 5),
      amount: booking.amount ? (booking.amount / 100).toString() : '',
      address: addressDisplay,
      notes: booking.notes || '',
      technicianId: booking.technicianId || '',
      status: booking.status || 'pending',
      poolId: booking.poolId || '',
      isPoolBooking: !!booking.poolId,
      paymentStatus: booking.paymentStatus || '',
      amountPaid: booking.amountPaid ? (booking.amountPaid / 100).toString() : '',
      remainingAmount: booking.remainingAmount ? (booking.remainingAmount / 100).toString() : '',
      vehicleId: (booking as any).vehicleId || '',
      vehicleInfo: {
        make: booking.vehicleInfo?.make || '',
        model: booking.vehicleInfo?.model || '',
        year: booking.vehicleInfo?.year?.toString() || '',
        color: booking.vehicleInfo?.color || '',
        licensePlate: booking.vehicleInfo?.licensePlate || '',
      },
      vehicles,
    });
    setIsBookingDrawerOpen(true);
  };

  const handleSaveBookingDetails = async () => {
    if (!editingBookingDetails) return;
    
    try {
      setSavingBooking(true);
      const scheduledDateTime = new Date(`${bookingEditForm.scheduledDate}T${bookingEditForm.scheduledTime}`);
      
      // Build update object conditionally - only include fields that have values
      // Firestore doesn't accept undefined, so we omit fields or use deleteField() to remove them
      const updatedBooking: any = {
        customerName: bookingEditForm.customerName,
        scheduledDate: Timestamp.fromDate(scheduledDateTime),
        scheduledTime: Timestamp.fromDate(scheduledDateTime),
        amount: parseFloat(bookingEditForm.amount) * 100, // Convert to cents
        // CRITICAL: Don't update address if it's being edited as a string - preserve existing address object
        // Address object structure must be preserved for map/navigation functionality
        // Only update address if we have a way to properly construct the address object
        // For now, we'll skip address updates from the edit form to prevent breaking the object structure
        // address: bookingEditForm.address, // REMOVED - prevents overwriting address object with string
        notes: bookingEditForm.notes || '',
        status: bookingEditForm.status,
        isPoolBooking: bookingEditForm.isPoolBooking,
        updatedAt: Timestamp.now(),
      };

      // Handle services: prefer services array, fallback to single serviceType
      if (bookingEditForm.services && bookingEditForm.services.length > 0) {
        // Multi-service booking
        updatedBooking.services = bookingEditForm.services
          .filter(s => s.serviceTypeId) // Only include services with a selected type
          .map(service => ({
            packageId: service.variantId 
              ? `${service.serviceTypeId}:${service.variantId}` 
              : service.serviceTypeId,
            addOns: service.addOns || [],
            vehicleCount: service.vehicleCount || 1,
            quantity: service.quantity || 1,
          }));
        
        // Also set serviceType to first service for backward compatibility
        if (updatedBooking.services.length > 0) {
          updatedBooking.serviceType = bookingEditForm.services[0].serviceTypeId;
          if (bookingEditForm.services[0].variantId) {
            updatedBooking.variantId = bookingEditForm.services[0].variantId;
          }
          if (bookingEditForm.services[0].addOns && bookingEditForm.services[0].addOns.length > 0) {
            updatedBooking.addOns = bookingEditForm.services[0].addOns;
          }
        }
      } else if (bookingEditForm.serviceType) {
        // Legacy single-service booking
        updatedBooking.serviceType = bookingEditForm.serviceType;
        if (bookingEditForm.variantId) {
          updatedBooking.variantId = bookingEditForm.variantId;
        } else if (editingBookingDetails?.variantId) {
          // If variant was previously set but is now cleared, delete it
          updatedBooking.variantId = deleteField();
        }
        if (bookingEditForm.addOns && bookingEditForm.addOns.length > 0) {
          updatedBooking.addOns = bookingEditForm.addOns;
        } else if (editingBookingDetails?.addOns && editingBookingDetails.addOns.length > 0) {
          // If addOns were previously set but are now cleared, set to empty array
          updatedBooking.addOns = [];
        }
      }

      // Handle vehicles: prefer vehicles array, fallback to single vehicleInfo
      if (bookingEditForm.vehicles && bookingEditForm.vehicles.length > 0) {
        // Multi-vehicle booking
        const filteredVehicles = bookingEditForm.vehicles
          .filter(v => v.vehicleInfo.make || v.vehicleInfo.model) // Only include vehicles with at least make or model
          .map(vehicle => {
            const vehicleData: any = {
              vehicleInfo: {
                make: vehicle.vehicleInfo.make || '',
                model: vehicle.vehicleInfo.model || '',
                color: vehicle.vehicleInfo.color || '',
                licensePlate: vehicle.vehicleInfo.licensePlate || '',
              },
            };
            
            // Only include vehicleId if it has a value
            if (vehicle.vehicleId && vehicle.vehicleId.trim() !== '') {
              vehicleData.vehicleId = vehicle.vehicleId;
            }
            
            // Only include year if it has a value
            if (vehicle.vehicleInfo.year && vehicle.vehicleInfo.year.trim() !== '') {
              const yearValue = parseInt(vehicle.vehicleInfo.year);
              if (!isNaN(yearValue)) {
                vehicleData.vehicleInfo.year = yearValue;
              }
            }
            
            return vehicleData;
          });
        
        if (filteredVehicles.length > 0) {
          updatedBooking.vehicles = filteredVehicles;
          
          // Also set vehicleInfo to first vehicle for backward compatibility
          updatedBooking.vehicleInfo = filteredVehicles[0].vehicleInfo;
          if (filteredVehicles[0].vehicleId) {
            updatedBooking.vehicleId = filteredVehicles[0].vehicleId;
          }
        }
      } else if (bookingEditForm.vehicleInfo.make || bookingEditForm.vehicleInfo.model) {
        // Legacy single-vehicle booking
        const vehicleInfo: any = {
          make: bookingEditForm.vehicleInfo.make || '',
          model: bookingEditForm.vehicleInfo.model || '',
          color: bookingEditForm.vehicleInfo.color || '',
          licensePlate: bookingEditForm.vehicleInfo.licensePlate || '',
        };
        
        // Only include year if it has a value
        if (bookingEditForm.vehicleInfo.year && bookingEditForm.vehicleInfo.year.trim() !== '') {
          const yearValue = parseInt(bookingEditForm.vehicleInfo.year);
          if (!isNaN(yearValue)) {
            vehicleInfo.year = yearValue;
          }
        }
        
        updatedBooking.vehicleInfo = vehicleInfo;
        
        if (bookingEditForm.vehicleId && bookingEditForm.vehicleId.trim() !== '') {
          updatedBooking.vehicleId = bookingEditForm.vehicleId;
        } else if (editingBookingDetails?.vehicleId) {
          // If vehicleId was previously set but is now cleared, delete it
          updatedBooking.vehicleId = deleteField();
        }
      }

      // Add payment fields if they have values
      if (bookingEditForm.paymentStatus) {
        updatedBooking.paymentStatus = bookingEditForm.paymentStatus;
      }
      if (bookingEditForm.amountPaid) {
        updatedBooking.amountPaid = Math.round(parseFloat(bookingEditForm.amountPaid) * 100); // Convert to cents
      }
      if (bookingEditForm.remainingAmount) {
        updatedBooking.remainingAmount = Math.round(parseFloat(bookingEditForm.remainingAmount) * 100); // Convert to cents
      }

      // Handle technicianId - include if it has a value, or delete if it was previously set but is now empty
      const originalTechnicianId = editingBookingDetails.technicianId;
      if (bookingEditForm.technicianId && bookingEditForm.technicianId.trim() !== '') {
        updatedBooking.technicianId = bookingEditForm.technicianId;
      } else if (originalTechnicianId) {
        // If technicianId was previously set but is now empty, explicitly delete it
        updatedBooking.technicianId = deleteField();
      }

      // Handle poolId and poolName - include if pool booking is enabled and poolId has a value
      // Or delete if pool booking is disabled or poolId is cleared
      const originalPoolId = editingBookingDetails.poolId;
      if (bookingEditForm.isPoolBooking && bookingEditForm.poolId && bookingEditForm.poolId.trim() !== '') {
        updatedBooking.poolId = bookingEditForm.poolId;
        const selectedPool = pools.find(p => p.id === bookingEditForm.poolId);
        updatedBooking.poolName = selectedPool?.name;
      } else if (originalPoolId) {
        // If pool booking is disabled or poolId is empty, but it was previously set, delete pool-related fields
        updatedBooking.poolId = deleteField();
        updatedBooking.poolName = deleteField();
      }

      await updateBooking(editingBookingDetails.id, updatedBooking);
      
      // Clean updatedBooking for local state - remove deleteField() objects and undefined values
      // deleteField() returns objects with _methodName that React can't render
      const cleanUpdatedBooking: any = {};
      for (const [key, value] of Object.entries(updatedBooking)) {
        // Skip deleteField() objects (they have _methodName property)
        if (value && typeof value === 'object' && '_methodName' in value) {
          // For local state, if it's a deleteField(), we should remove the field (set to undefined)
          // But we'll just omit it from the update
          continue;
        }
        if (value !== undefined) {
          cleanUpdatedBooking[key] = value;
        }
      }
      
      // Update local state with cleaned data
      setBookings(bookings.map(b => {
        if (b.id === editingBookingDetails.id) {
          const updated = { ...b, ...cleanUpdatedBooking };
          // Remove fields that were deleted (technicianId, poolId, poolName)
          if (updatedBooking.technicianId && typeof updatedBooking.technicianId === 'object' && '_methodName' in updatedBooking.technicianId) {
            delete updated.technicianId;
          }
          if (updatedBooking.poolId && typeof updatedBooking.poolId === 'object' && '_methodName' in updatedBooking.poolId) {
            delete updated.poolId;
            delete updated.poolName;
          }
          return updated;
        }
        return b;
      }));
      
      // Update viewing booking
      const updatedViewingBooking = { ...viewingBooking!, ...cleanUpdatedBooking };
      if (updatedBooking.technicianId && typeof updatedBooking.technicianId === 'object' && '_methodName' in updatedBooking.technicianId) {
        delete updatedViewingBooking.technicianId;
      }
      if (updatedBooking.poolId && typeof updatedBooking.poolId === 'object' && '_methodName' in updatedBooking.poolId) {
        delete updatedViewingBooking.poolId;
        delete updatedViewingBooking.poolName;
      }
      setViewingBooking(updatedViewingBooking);
      
      toast({
        title: 'Booking updated',
        description: 'Booking details have been updated successfully',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      setIsEditingBooking(false);
    } catch (error) {
      console.error('Error updating booking:', error);
      toast({
        title: 'Error updating booking',
        description: 'Failed to update booking details',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setSavingBooking(false);
    }
  };

  const handleCancelEdit = () => {
    setIsEditingBooking(false);
    // Reset form to original values
    if (viewingBooking) {
      const scheduledDate = viewingBooking.scheduledTime || viewingBooking.scheduledDate;
      let date: Date;
      if (scheduledDate && typeof scheduledDate === 'object' && 'toDate' in scheduledDate) {
        date = scheduledDate.toDate();
      } else {
        date = new Date(scheduledDate);
      }
      // Convert services array to form format if it exists
      let services: FormServiceSelection[] = [];
      if (viewingBooking.services && Array.isArray(viewingBooking.services) && viewingBooking.services.length > 0) {
        services = viewingBooking.services.map((s: any) => {
          const [serviceTypeId, variantId] = s.packageId?.includes(':') 
            ? s.packageId.split(':') 
            : [s.packageId || '', ''];
          return {
            serviceTypeId,
            variantId: variantId || '',
            addOns: s.addOns || [],
            vehicleCount: s.vehicleCount || 1,
            quantity: s.quantity || 1,
          };
        });
      }

      // Convert vehicles array to form format if it exists
      let vehicles: Array<{
        vehicleId?: string;
        vehicleInfo: {
          make: string;
          model: string;
          year: string;
          color: string;
          licensePlate: string;
        };
      }> = [];
      if (viewingBooking.vehicles && Array.isArray(viewingBooking.vehicles) && viewingBooking.vehicles.length > 0) {
        vehicles = viewingBooking.vehicles.map((v: any) => ({
          vehicleId: v.vehicleId || '',
          vehicleInfo: {
            make: v.vehicleInfo?.make || '',
            model: v.vehicleInfo?.model || '',
            year: v.vehicleInfo?.year?.toString() || '',
            color: v.vehicleInfo?.color || '',
            licensePlate: v.vehicleInfo?.licensePlate || '',
          },
        }));
      } else if (viewingBooking.vehicleInfo) {
        // Convert legacy single vehicle to array format
        vehicles = [{
          vehicleId: (viewingBooking as any).vehicleId || '',
          vehicleInfo: {
            make: viewingBooking.vehicleInfo.make || '',
            model: viewingBooking.vehicleInfo.model || '',
            year: viewingBooking.vehicleInfo.year?.toString() || '',
            color: viewingBooking.vehicleInfo.color || '',
            licensePlate: viewingBooking.vehicleInfo.licensePlate || '',
          },
        }];
      }

      setBookingEditForm({
        customerName: viewingBooking.customerName || '',
        serviceType: viewingBooking.serviceType || '',
        variantId: viewingBooking.variantId || '',
        addOns: viewingBooking.addOns || [],
        services,
        scheduledDate: date.toISOString().split('T')[0],
        scheduledTime: date.toTimeString().slice(0, 5),
        amount: viewingBooking.amount ? (viewingBooking.amount / 100).toString() : '',
        // Format address for display - preserve object structure understanding
        address: (() => {
          if (typeof viewingBooking.address === 'object' && viewingBooking.address !== null) {
            const addr = viewingBooking.address as any;
            const street = addr.street || addr.line1 || '';
            const city = addr.city || '';
            const state = addr.state || '';
            const zip = addr.zipCode || '';
            return [street, city, state, zip].filter(Boolean).join(', ');
          } else if (typeof viewingBooking.address === 'string') {
            return viewingBooking.address;
          }
          return '';
        })(),
        notes: viewingBooking.notes || '',
        technicianId: viewingBooking.technicianId || '',
        status: viewingBooking.status || 'pending',
        poolId: viewingBooking.poolId || '',
        isPoolBooking: !!viewingBooking.poolId,
        paymentStatus: viewingBooking.paymentStatus || '',
        amountPaid: viewingBooking.amountPaid ? (viewingBooking.amountPaid / 100).toString() : '',
        remainingAmount: viewingBooking.remainingAmount ? (viewingBooking.remainingAmount / 100).toString() : '',
        vehicleId: (viewingBooking as any).vehicleId || '',
        vehicleInfo: {
          make: viewingBooking.vehicleInfo?.make || '',
          model: viewingBooking.vehicleInfo?.model || '',
          year: viewingBooking.vehicleInfo?.year?.toString() || '',
          color: viewingBooking.vehicleInfo?.color || '',
          licensePlate: viewingBooking.vehicleInfo?.licensePlate || '',
        },
        vehicles,
      });
    }
  };

  if (loading) {
    return (
      <Center h="400px">
        <Spinner size="xl" />
      </Center>
    );
  }

  return (
    <Box p={6}>
      <Tabs>
        <TabList>
          <Tab>Calendar</Tab>
          <Tab>Bookings</Tab>
          <Tab>Analytics</Tab>
          <Tab>Pools</Tab>
          <Tab>Gift Cards</Tab>
        </TabList>

        <TabPanels>
          {/* Calendar Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading 
                size={{ base: 'md', md: 'lg' }}
                pl={{ base: '60px', lg: 0 }}
                pr={{ base: 4, lg: 0 }}
              >
                Bookings Calendar
              </Heading>
              
              <BookingsCalendar
                bookings={bookings}
                technicians={technicians}
                onBookingClick={handleViewBooking}
              />
            </VStack>
          </TabPanel>

          {/* Bookings Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              {/* Header */}
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                <Heading 
                  size={{ base: 'md', md: 'lg' }}
                  pl={{ base: '60px', lg: 0 }}
                  pr={{ base: 4, lg: 0 }}
                  flex="1"
                  minW="200px"
                >
                  Bookings List
                </Heading>
                <ButtonGroup>
                  <Button
                    leftIcon={<FiPlusIcon />}
                    colorScheme="blue"
                    onClick={() => setIsCreateOpen(true)}
                  >
                    Create Booking
                  </Button>
                  <Button
                    colorScheme="blue"
                    variant="outline"
                    onClick={() => setIsExportOpen(true)}
                  >
                    Export
                  </Button>
                  {selectedBookings.length > 0 && (
                    <Button
                      colorScheme="orange"
                      onClick={onBulkOpen}
                    >
                      Bulk Update ({selectedBookings.length})
                    </Button>
                  )}
                </ButtonGroup>
              </Flex>

              {/* Stats Cards */}
              <SimpleGrid columns={{ base: 2, md: 4, lg: 8 }} spacing={4}>
                <Stat>
                  <StatLabel>Total</StatLabel>
                  <StatNumber>{stats.total}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Pending</StatLabel>
                  <StatNumber color="yellow.500">{stats.pending}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Confirmed</StatLabel>
                  <StatNumber color="blue.500">{stats.confirmed}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>In Progress</StatLabel>
                  <StatNumber color="orange.500">{stats.in_progress}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Completed</StatLabel>
                  <StatNumber color="green.500">{stats.completed}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Cancelled</StatLabel>
                  <StatNumber color="red.500">{stats.cancelled}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Pool Bookings</StatLabel>
                  <StatNumber color="purple.500">{stats.poolBookings}</StatNumber>
                </Stat>
                <Stat>
                  <StatLabel>Individual</StatLabel>
                  <StatNumber color="teal.500">{stats.individualBookings}</StatNumber>
                </Stat>
              </SimpleGrid>

              {/* Filters */}
              <HStack spacing={4}>
                <Input
                  placeholder="Search bookings..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  maxW="300px"
                />
                <Select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  maxW="200px"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
                <Select
                  value={poolFilter}
                  onChange={(e) => setPoolFilter(e.target.value)}
                  maxW="200px"
                >
                  <option value="all">All Bookings</option>
                  <option value="pool">Pool Bookings</option>
                  <option value="individual">Individual Bookings</option>
                  {pools.map(pool => (
                    <option key={pool.id} value={pool.id}>{pool.name}</option>
                  ))}
                </Select>
              </HStack>

              {/* Bookings Table */}
              <Box 
                overflowX="auto" 
                overflowY="auto"
                maxH="calc(100vh - 400px)"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Table variant="simple" minW="100%">
                  <Thead>
                    <Tr>
                      <Th>
                        <Checkbox
                          isChecked={selectedBookings.length === filteredBookings.length && filteredBookings.length > 0}
                          isIndeterminate={selectedBookings.length > 0 && selectedBookings.length < filteredBookings.length}
                          onChange={(e) => handleSelectAll(e.target.checked)}
                        />
                      </Th>
                      <Th>Customer</Th>
                      <Th>Service</Th>
                      <Th>Date & Time</Th>
                      <Th>Status</Th>
                      <Th>Amount</Th>
                      <Th>Pool</Th>
                      <Th>Technician</Th>
                      <Th>Actions</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredBookings.map((booking) => (
                      <Tr key={booking.id}>
                        <Td>
                          <Checkbox
                            isChecked={selectedBookings.includes(booking.id)}
                            onChange={(e) => handleSelectBooking(booking.id, e.target.checked)}
                          />
                        </Td>
                        <Td>{getCustomerName(booking)}</Td>
                        <Td>
                          {(() => {
                            const serviceDetails = getServiceDetails(booking);
                            return (
                              <VStack align="start" spacing={0}>
                                <Text>{serviceDetails.displayName}</Text>
                                {serviceDetails.addOnNames.length > 0 && (
                                  <Text fontSize="xs" color="gray.500">
                                    Add-ons: {serviceDetails.addOnNames.join(', ')}
                                  </Text>
                                )}
                              </VStack>
                            );
                          })()}
                        </Td>
                        <Td>{formatDate(booking.scheduledTime || booking.scheduledDate)}</Td>
                        <Td>
                          <Badge colorScheme={getStatusColor(booking.status)}>
                            {booking.status}
                          </Badge>
                        </Td>
                        <Td>
                          {formatAmount(booking.amount)}
                          {booking.poolDiscount && (
                            <Text fontSize="xs" color="green.500">
                              -{formatAmount(booking.poolDiscount)} (Pool)
                            </Text>
                          )}
                        </Td>
                        <Td>
                          {booking.poolId ? (
                            <Badge colorScheme="purple" variant="subtle">
                              {booking.poolName || 'Pool Booking'}
                            </Badge>
                          ) : (
                            <Text fontSize="sm" color="gray.500">Individual</Text>
                          )}
                        </Td>
                        <Td>{getTechnicianName(booking.technicianId || '')}</Td>
                        <Td>
                          <HStack spacing={2}>
                            <Tooltip label="Edit booking">
                              <IconButton
                                aria-label="Edit booking"
                                icon={<FiEdit2Icon />}
                                size="sm"
                                onClick={() => handleViewBooking(booking)}
                              />
                            </Tooltip>
                          </HStack>
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </VStack>
          </TabPanel>

          {/* Analytics Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading 
                size={{ base: 'md', md: 'lg' }}
                pl={{ base: '60px', lg: 0 }}
                pr={{ base: 4, lg: 0 }}
              >
                Analytics
              </Heading>
              
              {/* Pool Analytics */}
              <Box>
                <Heading size="md" mb={4}>Pool Analytics</Heading>
                {poolAnalytics && (
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Card>
                      <CardHeader>
                        <Heading size="md">Pool Performance</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={4}>
                          <Stat>
                            <StatLabel>Average Pool Size</StatLabel>
                            <StatNumber>{(poolAnalytics.averagePoolSize ?? 0).toFixed(1)}</StatNumber>
                            <StatHelpText>participants per pool</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Average Discount</StatLabel>
                            <StatNumber>{(poolAnalytics.averagePoolDiscount ?? 0).toFixed(1)}%</StatNumber>
                            <StatHelpText>discount per pool</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Total Pool Revenue</StatLabel>
                            <StatNumber color="green.500">{formatAmount(poolAnalytics.totalPoolRevenue)}</StatNumber>
                            <StatHelpText>from all pools</StatHelpText>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader>
                        <Heading size="md">Pool Status Distribution</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3}>
                          {Object.entries(poolAnalytics.poolsByStatus || {}).map(([status, count]) => (
                            <HStack key={status} justify="space-between" w="full">
                              <Text fontSize="sm" textTransform="capitalize">{status}</Text>
                              <Badge colorScheme={getPoolStatusColor(status as any)}>{count as number}</Badge>
                            </HStack>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                )}
              </Box>

              {/* Gift Card Analytics */}
              <Box>
                <Heading size="md" mb={4}>Gift Card Analytics</Heading>
                {giftCardAnalytics && (
                  <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                    <Card>
                      <CardHeader>
                        <Heading size="sm">Overview</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Stat>
                            <StatLabel>Total Gift Cards</StatLabel>
                            <StatNumber>{giftCardAnalytics.totalGiftCards}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Total Purchased</StatLabel>
                            <StatNumber color="blue.500">{giftCardAnalytics.totalPurchased}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Total Redeemed</StatLabel>
                            <StatNumber color="green.500">{giftCardAnalytics.totalRedeemed}</StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader>
                        <Heading size="sm">Financial Metrics</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Stat>
                            <StatLabel>Total Revenue</StatLabel>
                            <StatNumber color="green.500">{formatAmount(giftCardAnalytics.totalRevenue)}</StatNumber>
                            <StatHelpText>from all purchases</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Total Redeemed Value</StatLabel>
                            <StatNumber color="teal.500">{formatAmount(giftCardAnalytics.totalRedeemedValue)}</StatNumber>
                            <StatHelpText>value of redeemed cards</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Average Denomination</StatLabel>
                            <StatNumber>{formatAmount(giftCardAnalytics.averageDenomination)}</StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader>
                        <Heading size="sm">Status & Performance</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3} align="stretch">
                          <Stat>
                            <StatLabel>Redemption Rate</StatLabel>
                            <StatNumber color="purple.500">{giftCardAnalytics.redemptionRate.toFixed(1)}%</StatNumber>
                            <StatHelpText>of purchased cards</StatHelpText>
                          </Stat>
                          <Stat>
                            <StatLabel>Pending</StatLabel>
                            <StatNumber color="yellow.500">{giftCardAnalytics.totalPending}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Expired</StatLabel>
                            <StatNumber color="orange.500">{giftCardAnalytics.totalExpired}</StatNumber>
                          </Stat>
                          <Stat>
                            <StatLabel>Cancelled</StatLabel>
                            <StatNumber color="red.500">{giftCardAnalytics.totalCancelled}</StatNumber>
                          </Stat>
                        </VStack>
                      </CardBody>
                    </Card>

                    <Card>
                      <CardHeader>
                        <Heading size="sm">Status Distribution</Heading>
                      </CardHeader>
                      <CardBody>
                        <VStack spacing={3}>
                          {Object.entries(giftCardAnalytics.giftCardsByStatus || {}).map(([status, count]) => (
                            <HStack key={status} justify="space-between" w="full">
                              <Text fontSize="sm" textTransform="capitalize">{status}</Text>
                              <Badge colorScheme={
                                status === 'redeemed' ? 'green' :
                                status === 'pending' ? 'yellow' :
                                status === 'sent' ? 'blue' :
                                status === 'expired' ? 'orange' :
                                status === 'cancelled' ? 'red' : 'gray'
                              }>{count as number}</Badge>
                            </HStack>
                          ))}
                        </VStack>
                      </CardBody>
                    </Card>
                  </SimpleGrid>
                )}
              </Box>
            </VStack>
          </TabPanel>

          {/* Pools Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Flex justify="space-between" align="center" flexWrap="wrap" gap={4}>
                <Heading 
                  size={{ base: 'md', md: 'lg' }}
                  pl={{ base: '60px', lg: 0 }}
                  pr={{ base: 4, lg: 0 }}
                  flex="1"
                  minW="200px"
                >
                  Booking Pools
                </Heading>
                <Button
                  leftIcon={<FiPlusIcon />}
                  colorScheme="purple"
                  onClick={() => setIsCreatePoolOpen(true)}
                >
                  Create Pool
                </Button>
              </Flex>

              {/* Pool Stats */}
              {poolAnalytics && (
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
                  <Stat>
                    <StatLabel>Total Pools</StatLabel>
                    <StatNumber>{totalPools}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Active Pools</StatLabel>
                    <StatNumber color="green.500">{activePools}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Completed Pools</StatLabel>
                    <StatNumber color="blue.500">{completedPools}</StatNumber>
                  </Stat>
                  <Stat>
                    <StatLabel>Total Revenue</StatLabel>
                    <StatNumber color="green.500">{formatAmount(totalPoolRevenue)}</StatNumber>
                  </Stat>
                </SimpleGrid>
              )}

              {/* Pools Grid */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {pools.map((pool) => {
                  const poolRevenue = bookings.filter(b => b.poolId === pool.id).reduce((sum, b) => sum + (typeof b.amount === 'number' && !isNaN(b.amount) ? b.amount : 0), 0);
                  return (
                    <Card key={pool.id} cursor="pointer" onClick={() => handleViewPool(pool)}>
                      <CardHeader>
                        <Flex justify="space-between" align="center">
                          <Heading size="md">{pool.name}</Heading>
                          <Badge colorScheme={getPoolStatusColor(pool.status)}>
                            {pool.status}
                          </Badge>
                          <IconButton
                            icon={<FiTrash2Icon />}
                            size="sm"
                            aria-label="Delete Pool"
                            colorScheme="red"
                            variant="ghost"
                            onClick={(e) => { e.stopPropagation(); handleDeletePool(pool.id); }}
                          />
                        </Flex>
                      </CardHeader>
                      <CardBody>
                        <VStack align="stretch" spacing={3}>
                          <Text fontSize="sm" color="gray.600">{pool.label}</Text>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Address:</Text>
                            <Text fontSize="sm">
                              {(pool.street || (pool.poolAddress && typeof pool.poolAddress === 'object' ? pool.poolAddress.street : '') || 'N/A')}, {(pool.city || (pool.poolAddress && typeof pool.poolAddress === 'object' ? pool.poolAddress.city : '') || 'N/A')}, {(pool.state || (pool.poolAddress && typeof pool.poolAddress === 'object' ? pool.poolAddress.state : '') || 'N/A')} {(pool.zipCode || (pool.poolAddress && typeof pool.poolAddress === 'object' ? pool.poolAddress.zipCode : '') || 'N/A')}
                            </Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Type:</Text>
                            <Text fontSize="sm">{pool.type}</Text>
                          </HStack>
                          <HStack justify="space-between">
                            <Text fontSize="sm">Status:</Text>
                            <Text fontSize="sm">{pool.status}</Text>
                          </HStack>
                          <Progress 
                            value={(poolBookings.length / (poolBookings.length || 1)) * 100} 
                            colorScheme="purple" 
                            size="sm"
                          />
                          <HStack justify="space-between">
                            <Text fontSize="sm">Revenue:</Text>
                            <Text fontSize="sm" fontWeight="bold">
                              {formatAmount(poolRevenue)}
                            </Text>
                          </HStack>
                        </VStack>
                      </CardBody>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </VStack>
          </TabPanel>

          {/* Gift Cards Tab */}
          <TabPanel>
            <VStack spacing={6} align="stretch">
              <Heading 
                size={{ base: 'md', md: 'lg' }}
                pl={{ base: '60px', lg: 0 }}
                pr={{ base: 4, lg: 0 }}
              >
                Gift Cards Management
              </Heading>

              {/* Filters */}
              <HStack spacing={4}>
                <Input
                  placeholder="Search gift cards by code, recipient, or purchaser..."
                  value={giftCardSearchTerm}
                  onChange={(e) => setGiftCardSearchTerm(e.target.value)}
                  maxW="400px"
                />
                <Select
                  value={giftCardStatusFilter}
                  onChange={(e) => setGiftCardStatusFilter(e.target.value)}
                  maxW="200px"
                >
                  <option value="all">All Status</option>
                  <option value="pending">Pending</option>
                  <option value="sent">Sent</option>
                  <option value="redeemed">Redeemed</option>
                  <option value="expired">Expired</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </HStack>

              {/* Gift Cards Table */}
              <Box 
                overflowX="auto" 
                overflowY="auto"
                maxH="calc(100vh - 400px)"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="md"
              >
                <Table variant="simple" minW="100%">
                  <Thead>
                    <Tr>
                      <Th>Code</Th>
                      <Th>Denomination</Th>
                      <Th>Purchaser</Th>
                      <Th>Recipient</Th>
                      <Th>Status</Th>
                      <Th>Purchased</Th>
                      <Th>Redeemed</Th>
                      <Th>Booking ID</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {filteredGiftCards.map((giftCard) => (
                      <Tr key={giftCard.id}>
                        <Td fontWeight="bold" fontFamily="monospace">{giftCard.code}</Td>
                        <Td fontWeight="bold">{formatAmount(giftCard.denomination)}</Td>
                        <Td>
                          {giftCard.purchaser ? (
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm">
                                {giftCard.purchaser.firstName} {giftCard.purchaser.lastName}
                              </Text>
                              <Text fontSize="xs" color="gray.600">{giftCard.purchaser.email}</Text>
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">Unknown</Text>
                          )}
                        </Td>
                        <Td>
                          <VStack align="start" spacing={0}>
                            <Text fontSize="sm">{giftCard.recipientName || 'N/A'}</Text>
                            <Text fontSize="xs" color="gray.600">{giftCard.recipientEmail}</Text>
                          </VStack>
                        </Td>
                        <Td>
                          <Badge colorScheme={
                            giftCard.status === 'redeemed' ? 'green' :
                            giftCard.status === 'pending' ? 'yellow' :
                            giftCard.status === 'sent' ? 'blue' :
                            giftCard.status === 'expired' ? 'orange' :
                            giftCard.status === 'cancelled' ? 'red' : 'gray'
                          }>
                            {giftCard.status}
                          </Badge>
                        </Td>
                        <Td>
                          {giftCard.purchasedAt ? (
                            <Text fontSize="sm">
                              {giftCard.purchasedAt.toDate ? giftCard.purchasedAt.toDate().toLocaleDateString() : 'N/A'}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500">N/A</Text>
                          )}
                        </Td>
                        <Td>
                          {giftCard.redeemedAt ? (
                            <VStack align="start" spacing={0}>
                              <Text fontSize="sm">
                                {giftCard.redeemedAt.toDate ? giftCard.redeemedAt.toDate().toLocaleDateString() : 'N/A'}
                              </Text>
                              {giftCard.redeemer && (
                                <Text fontSize="xs" color="gray.600">
                                  {giftCard.redeemer.firstName} {giftCard.redeemer.lastName}
                                </Text>
                              )}
                            </VStack>
                          ) : (
                            <Text fontSize="sm" color="gray.500">Not redeemed</Text>
                          )}
                        </Td>
                        <Td>
                          {giftCard.redeemedForBookingId ? (
                            <Text fontSize="sm" fontFamily="monospace" color="blue.500">
                              {giftCard.redeemedForBookingId.slice(-8)}
                            </Text>
                          ) : (
                            <Text fontSize="sm" color="gray.500">N/A</Text>
                          )}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>

              {filteredGiftCards.length === 0 && (
                <Center py={8}>
                  <Text color="gray.500">No gift cards found</Text>
                </Center>
              )}
            </VStack>
          </TabPanel>
        </TabPanels>
      </Tabs>

      {/* Edit Booking Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Edit Booking</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select value={editingStatus} onChange={(e) => setEditingStatus(e.target.value)}>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Technician</FormLabel>
                <Select value={editingTechnician} onChange={(e) => setEditingTechnician(e.target.value)}>
                  <option value="">Unassigned</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.firstName} {tech.lastName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea value={editingNotes} onChange={(e) => setEditingNotes(e.target.value)} />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleUpdateBooking} isLoading={updating}>
              Update
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Booking Modal */}
      <Modal isOpen={isCreateOpen} onClose={() => {
        setIsCreateOpen(false);
        setSelectedCustomer(null);
        setCustomerSearchTerm('');
        setCustomerAddresses([]);
        setCustomerVehicles([]);
      }} size={{ base: 'full', md: 'xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Booking</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Customer</FormLabel>
                <Box position="relative">
                  <Input
                    value={customerSearchTerm}
                    onChange={(e) => setCustomerSearchTerm(e.target.value)}
                    placeholder="Search customers by name or email..."
                    onFocus={() => setCustomerSearchTerm('')}
                  />
                  {customerSearchTerm && filteredCustomers.length > 0 && (
                    <Box
                      position="absolute"
                      top="100%"
                      left={0}
                      right={0}
                      zIndex={1000}
                      bg="white"
                      border="1px"
                      borderColor="gray.200"
                      borderRadius="md"
                      mt={1}
                      maxH="200px"
                      overflowY="auto"
                      boxShadow="lg"
                    >
                      {filteredCustomers.slice(0, 10).map((customer) => (
                        <Box
                          key={customer.id}
                          p={3}
                          cursor="pointer"
                          _hover={{ bg: 'gray.100' }}
                          onClick={() => {
                            handleCustomerSelect(customer);
                            setCustomerSearchTerm('');
                          }}
                        >
                          <Text fontWeight="medium">
                            {customer.firstName || customer.lastName
                              ? `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
                              : customer.email}
                          </Text>
                          {customer.email && (
                            <Text fontSize="sm" color="gray.600">{customer.email}</Text>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
                {selectedCustomer && (
                  <Box mt={2} p={2} bg="blue.50" borderRadius="md">
                    <Text fontSize="sm" fontWeight="medium">
                      Selected: {selectedCustomer.firstName || selectedCustomer.lastName
                        ? `${selectedCustomer.firstName || ''} ${selectedCustomer.lastName || ''}`.trim()
                        : selectedCustomer.email}
                    </Text>
                    <Button
                      size="xs"
                      mt={1}
                      onClick={() => handleCustomerSelect(null)}
                    >
                      Clear Selection
                    </Button>
                  </Box>
                )}
              </FormControl>
              
              <FormControl isRequired>
                <FormLabel>Customer Name</FormLabel>
                <Input
                  value={createForm.customerName}
                  onChange={(e) => handleCreateFormChange('customerName', e.target.value)}
                  placeholder="Enter customer name"
                  isDisabled={!!selectedCustomer}
                />
              </FormControl>
              
              {selectedCustomer && customerAddresses.length > 0 && (
                <FormControl>
                  <FormLabel>Select Address</FormLabel>
                  <Select
                    placeholder="Select an address"
                    value={createForm.selectedAddress?.id || ''}
                    onChange={(e) => {
                      const addressId = e.target.value;
                      const address = customerAddresses.find(a => a.id === addressId);
                      if (address) {
                        const addressString = `${address.street || address.line1 || ''}, ${address.city || ''}, ${address.state || ''} ${address.zipCode || ''}`.trim();
                        setCreateForm(prev => ({ 
                          ...prev, 
                          address: addressString,
                          selectedAddress: address,
                        }));
                      }
                    }}
                  >
                    {customerAddresses.map((address) => (
                      <option key={address.id} value={address.id}>
                        {address.label || 'Address'}: {address.street || address.line1 || ''}, {address.city || ''}, {address.state || ''} {address.zipCode || ''}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {selectedCustomer && customerVehicles.length > 0 && (
                <FormControl>
                  <FormLabel>Select Vehicle</FormLabel>
                  <Select
                    placeholder="Select a vehicle"
                    onChange={(e) => {
                      const vehicleId = e.target.value;
                      const vehicle = customerVehicles.find(v => v.id === vehicleId);
                      if (vehicle) {
                        setCreateForm(prev => ({
                          ...prev,
                          vehicleId: vehicle.id,
                          vehicleInfo: {
                            make: vehicle.make || '',
                            model: vehicle.model || '',
                            year: vehicle.year?.toString() || '',
                            color: vehicle.color || '',
                            licensePlate: vehicle.licensePlate || '',
                          },
                        }));
                      }
                    }}
                  >
                    {customerVehicles.map((vehicle) => (
                      <option key={vehicle.id} value={vehicle.id}>
                        {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.color ? `(${vehicle.color})` : ''} {vehicle.licensePlate ? `- ${vehicle.licensePlate}` : ''}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              
              {loadingCustomerData && (
                <Box textAlign="center" py={2}>
                  <Spinner size="sm" />
                  <Text fontSize="sm" color="gray.600" mt={2}>Loading customer data...</Text>
                </Box>
              )}
              <FormControl isRequired>
                <FormLabel>Service Type</FormLabel>
                <Select
                  value={createForm.serviceType}
                  onChange={(e) => {
                    handleCreateFormChange('serviceType', e.target.value);
                    // Reset variant and add-ons when service type changes
                    setCreateForm(prev => ({ ...prev, variantId: '', addOns: [] }));
                  }}
                  placeholder="Select service type"
                >
                  <option value="">Select service type</option>
                  {serviceTypes.map(st => (
                    <option key={st.id} value={st.id}>{st.name}</option>
                  ))}
                </Select>
              </FormControl>
              
              {/* Variant Selection */}
              {(() => {
                const selectedServiceType = serviceTypes.find(st => st.id === createForm.serviceType);
                if (selectedServiceType?.variants && selectedServiceType.variants.length > 0) {
                  const activeVariants = selectedServiceType.variants.filter(v => v.isActive !== false);
                  if (activeVariants.length > 0) {
                    return (
                      <FormControl>
                        <FormLabel>Service Variant</FormLabel>
                        <Select
                          value={createForm.variantId}
                          onChange={(e) => handleCreateFormChange('variantId', e.target.value)}
                          placeholder="Select variant (optional)"
                        >
                          <option value="">No variant</option>
                          {activeVariants.map(variant => (
                            <option key={variant.id} value={variant.id}>
                              {variant.name} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ''}
                            </option>
                          ))}
                        </Select>
                      </FormControl>
                    );
                  }
                }
                return null;
              })()}

              {/* Add-ons Selection */}
              {(() => {
                const selectedServiceType = serviceTypes.find(st => st.id === createForm.serviceType);
                if (selectedServiceType?.extras && selectedServiceType.extras.length > 0) {
                  const activeExtras = selectedServiceType.extras.filter(e => e.isActive !== false);
                  if (activeExtras.length > 0) {
                    return (
                      <FormControl>
                        <FormLabel>Add-ons</FormLabel>
                        <VStack align="start" spacing={2} p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200">
                          {activeExtras.map(extra => (
                            <Checkbox
                              key={extra.id}
                              isChecked={createForm.addOns.includes(extra.id)}
                              onChange={(e) => {
                                const isChecked = e.target.checked;
                                setCreateForm(prev => ({
                                  ...prev,
                                  addOns: isChecked
                                    ? [...prev.addOns, extra.id]
                                    : prev.addOns.filter(id => id !== extra.id)
                                }));
                              }}
                            >
                              {extra.name} {extra.price > 0 ? `(+$${extra.price.toFixed(2)})` : ''}
                            </Checkbox>
                          ))}
                        </VStack>
                      </FormControl>
                    );
                  }
                }
                return null;
              })()}

              {/* Multi-Service Section */}
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50">
                <HStack justify="space-between" mb={3}>
                  <FormLabel mb={0} fontWeight="semibold">Services</FormLabel>
                  <Button
                    size="sm"
                    leftIcon={<FiPlusIcon />}
                    onClick={() => {
                      setCreateForm(prev => ({
                        ...prev,
                        services: [...prev.services, {
                          serviceTypeId: '',
                          variantId: '',
                          addOns: [],
                          vehicleCount: 1,
                          quantity: 1,
                        }]
                      }));
                    }}
                  >
                    Add Service
                  </Button>
                </HStack>
                {createForm.services.length === 0 ? (
                  <Text fontSize="sm" color="gray.600" fontStyle="italic">
                    No services added. Click "Add Service" to add multiple services, or use the single service fields above.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {createForm.services.map((service, index) => (
                      <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="white">
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="semibold">Service {index + 1}</Text>
                          <IconButton
                            aria-label="Remove service"
                            icon={<FiTrash2Icon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              setCreateForm(prev => ({
                                ...prev,
                                services: prev.services.filter((_, i) => i !== index)
                              }));
                            }}
                          />
                        </HStack>
                        <VStack align="stretch" spacing={2}>
                          <Select
                            placeholder="Select service type"
                            value={service.serviceTypeId}
                            onChange={(e) => {
                              const updatedServices = [...createForm.services];
                              updatedServices[index] = {
                                ...service,
                                serviceTypeId: e.target.value,
                                variantId: '', // Reset variant when service changes
                                addOns: [], // Reset add-ons when service changes
                              };
                              setCreateForm(prev => ({ ...prev, services: updatedServices }));
                            }}
                          >
                            <option value="">Select service type</option>
                            {serviceTypes.map(st => (
                              <option key={st.id} value={st.id}>{st.name}</option>
                            ))}
                          </Select>
                          
                          {/* Variant for this service */}
                          {(() => {
                            const selectedServiceType = serviceTypes.find(st => st.id === service.serviceTypeId);
                            if (selectedServiceType?.variants && selectedServiceType.variants.length > 0) {
                              const activeVariants = selectedServiceType.variants.filter(v => v.isActive !== false);
                              if (activeVariants.length > 0) {
                                return (
                                  <Select
                                    placeholder="Select variant (optional)"
                                    value={service.variantId}
                                    onChange={(e) => {
                                      const updatedServices = [...createForm.services];
                                      updatedServices[index] = { ...service, variantId: e.target.value };
                                      setCreateForm(prev => ({ ...prev, services: updatedServices }));
                                    }}
                                  >
                                    <option value="">No variant</option>
                                    {activeVariants.map(variant => (
                                      <option key={variant.id} value={variant.id}>
                                        {variant.name} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ''}
                                      </option>
                                    ))}
                                  </Select>
                                );
                              }
                            }
                            return null;
                          })()}

                          {/* Add-ons for this service */}
                          {(() => {
                            const selectedServiceType = serviceTypes.find(st => st.id === service.serviceTypeId);
                            if (selectedServiceType?.extras && selectedServiceType.extras.length > 0) {
                              const activeExtras = selectedServiceType.extras.filter(e => e.isActive !== false);
                              if (activeExtras.length > 0) {
                                return (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="semibold" mb={1}>Add-ons:</Text>
                                    <VStack align="start" spacing={1}>
                                      {activeExtras.map(extra => (
                                        <Checkbox
                                          key={extra.id}
                                          size="sm"
                                          isChecked={service.addOns.includes(extra.id)}
                                          onChange={(e) => {
                                            const isChecked = e.target.checked;
                                            const updatedServices = [...createForm.services];
                                            updatedServices[index] = {
                                              ...service,
                                              addOns: isChecked
                                                ? [...service.addOns, extra.id]
                                                : service.addOns.filter(id => id !== extra.id)
                                            };
                                            setCreateForm(prev => ({ ...prev, services: updatedServices }));
                                          }}
                                        >
                                          {extra.name} {extra.price > 0 ? `(+$${extra.price.toFixed(2)})` : ''}
                                        </Checkbox>
                                      ))}
                                    </VStack>
                                  </Box>
                                );
                              }
                            }
                            return null;
                          })()}

                          <HStack>
                            <FormControl>
                              <FormLabel fontSize="sm">Vehicle Count</FormLabel>
                              <Input
                                type="number"
                                min="1"
                                value={service.vehicleCount}
                                onChange={(e) => {
                                  const updatedServices = [...createForm.services];
                                  updatedServices[index] = {
                                    ...service,
                                    vehicleCount: parseInt(e.target.value) || 1,
                                  };
                                  setCreateForm(prev => ({ ...prev, services: updatedServices }));
                                }}
                              />
                            </FormControl>
                            <FormControl>
                              <FormLabel fontSize="sm">Quantity</FormLabel>
                              <Input
                                type="number"
                                min="1"
                                value={service.quantity}
                                onChange={(e) => {
                                  const updatedServices = [...createForm.services];
                                  updatedServices[index] = {
                                    ...service,
                                    quantity: parseInt(e.target.value) || 1,
                                  };
                                  setCreateForm(prev => ({ ...prev, services: updatedServices }));
                                }}
                              />
                            </FormControl>
                          </HStack>
                        </VStack>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              {/* Multi-Vehicle Section */}
              <Box p={4} borderWidth="1px" borderRadius="md" borderColor="green.200" bg="green.50">
                <HStack justify="space-between" mb={3}>
                  <FormLabel mb={0} fontWeight="semibold">Vehicles</FormLabel>
                  <Button
                    size="sm"
                    leftIcon={<FiPlusIcon />}
                    onClick={() => {
                      setCreateForm(prev => ({
                        ...prev,
                        vehicles: [...prev.vehicles, {
                          vehicleInfo: {
                            make: '',
                            model: '',
                            year: '',
                            color: '',
                            licensePlate: '',
                          }
                        }]
                      }));
                    }}
                  >
                    Add Vehicle
                  </Button>
                </HStack>
                {createForm.vehicles.length === 0 ? (
                  <Text fontSize="sm" color="gray.600" fontStyle="italic" mb={3}>
                    No vehicles added. Click "Add Vehicle" to add multiple vehicles, or use the single vehicle fields below.
                  </Text>
                ) : (
                  <VStack align="stretch" spacing={3}>
                    {createForm.vehicles.map((vehicle, index) => (
                      <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="white">
                        <HStack justify="space-between" mb={2}>
                          <Text fontWeight="semibold">Vehicle {index + 1}</Text>
                          <IconButton
                            aria-label="Remove vehicle"
                            icon={<FiTrash2Icon />}
                            size="sm"
                            colorScheme="red"
                            variant="ghost"
                            onClick={() => {
                              setCreateForm(prev => ({
                                ...prev,
                                vehicles: prev.vehicles.filter((_, i) => i !== index)
                              }));
                            }}
                          />
                        </HStack>
                        <SimpleGrid columns={2} spacing={2}>
                          <FormControl>
                            <FormLabel fontSize="sm">Make</FormLabel>
                            <Input
                              size="sm"
                              value={vehicle.vehicleInfo.make}
                              onChange={(e) => {
                                const updatedVehicles = [...createForm.vehicles];
                                updatedVehicles[index] = {
                                  ...vehicle,
                                  vehicleInfo: { ...vehicle.vehicleInfo, make: e.target.value }
                                };
                                setCreateForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Model</FormLabel>
                            <Input
                              size="sm"
                              value={vehicle.vehicleInfo.model}
                              onChange={(e) => {
                                const updatedVehicles = [...createForm.vehicles];
                                updatedVehicles[index] = {
                                  ...vehicle,
                                  vehicleInfo: { ...vehicle.vehicleInfo, model: e.target.value }
                                };
                                setCreateForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Year</FormLabel>
                            <Input
                              size="sm"
                              type="number"
                              value={vehicle.vehicleInfo.year}
                              onChange={(e) => {
                                const updatedVehicles = [...createForm.vehicles];
                                updatedVehicles[index] = {
                                  ...vehicle,
                                  vehicleInfo: { ...vehicle.vehicleInfo, year: e.target.value }
                                };
                                setCreateForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                              }}
                            />
                          </FormControl>
                          <FormControl>
                            <FormLabel fontSize="sm">Color</FormLabel>
                            <Input
                              size="sm"
                              value={vehicle.vehicleInfo.color}
                              onChange={(e) => {
                                const updatedVehicles = [...createForm.vehicles];
                                updatedVehicles[index] = {
                                  ...vehicle,
                                  vehicleInfo: { ...vehicle.vehicleInfo, color: e.target.value }
                                };
                                setCreateForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                              }}
                            />
                          </FormControl>
                          <FormControl gridColumn="span 2">
                            <FormLabel fontSize="sm">License Plate</FormLabel>
                            <Input
                              size="sm"
                              value={vehicle.vehicleInfo.licensePlate}
                              onChange={(e) => {
                                const updatedVehicles = [...createForm.vehicles];
                                updatedVehicles[index] = {
                                  ...vehicle,
                                  vehicleInfo: { ...vehicle.vehicleInfo, licensePlate: e.target.value }
                                };
                                setCreateForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                              }}
                            />
                          </FormControl>
                        </SimpleGrid>
                      </Box>
                    ))}
                  </VStack>
                )}
              </Box>

              {/* Legacy Single Vehicle Fields (for backward compatibility) */}
              <Box p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200" bg="gray.50">
                <Text fontSize="sm" fontWeight="semibold" mb={2}>Single Vehicle (Legacy)</Text>
                <SimpleGrid columns={2} spacing={2}>
                  <FormControl>
                    <FormLabel fontSize="sm">Make</FormLabel>
                    <Input
                      size="sm"
                      value={createForm.vehicleInfo.make}
                      onChange={(e) => setCreateForm(prev => ({
                        ...prev,
                        vehicleInfo: { ...prev.vehicleInfo, make: e.target.value }
                      }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Model</FormLabel>
                    <Input
                      size="sm"
                      value={createForm.vehicleInfo.model}
                      onChange={(e) => setCreateForm(prev => ({
                        ...prev,
                        vehicleInfo: { ...prev.vehicleInfo, model: e.target.value }
                      }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Year</FormLabel>
                    <Input
                      size="sm"
                      type="number"
                      value={createForm.vehicleInfo.year}
                      onChange={(e) => setCreateForm(prev => ({
                        ...prev,
                        vehicleInfo: { ...prev.vehicleInfo, year: e.target.value }
                      }))}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm">Color</FormLabel>
                    <Input
                      size="sm"
                      value={createForm.vehicleInfo.color}
                      onChange={(e) => setCreateForm(prev => ({
                        ...prev,
                        vehicleInfo: { ...prev.vehicleInfo, color: e.target.value }
                      }))}
                    />
                  </FormControl>
                  <FormControl gridColumn="span 2">
                    <FormLabel fontSize="sm">License Plate</FormLabel>
                    <Input
                      size="sm"
                      value={createForm.vehicleInfo.licensePlate}
                      onChange={(e) => setCreateForm(prev => ({
                        ...prev,
                        vehicleInfo: { ...prev.vehicleInfo, licensePlate: e.target.value }
                      }))}
                    />
                  </FormControl>
                </SimpleGrid>
              </Box>

              <HStack spacing={4} w="full">
                <FormControl isRequired>
                  <FormLabel>Date</FormLabel>
                  <Input
                    type="date"
                    value={createForm.scheduledDate}
                    onChange={(e) => handleCreateFormChange('scheduledDate', e.target.value)}
                  />
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Time</FormLabel>
                  <Input
                    type="time"
                    value={createForm.scheduledTime}
                    onChange={(e) => handleCreateFormChange('scheduledTime', e.target.value)}
                  />
                </FormControl>
              </HStack>
              <FormControl isRequired>
                <FormLabel>Amount</FormLabel>
                <Input
                  type="number"
                  value={createForm.amount}
                  onChange={(e) => handleCreateFormChange('amount', e.target.value)}
                  placeholder="Enter amount"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Address</FormLabel>
                <Input
                  value={typeof createForm.address === 'string' ? createForm.address : (createForm.selectedAddress ? `${createForm.selectedAddress.street || createForm.selectedAddress.line1 || ''}, ${createForm.selectedAddress.city || ''}, ${createForm.selectedAddress.state || ''} ${createForm.selectedAddress.zipCode || ''}`.trim() : '')}
                  onChange={(e) => {
                    handleCreateFormChange('address', e.target.value);
                    // Clear selected address when manually typing
                    if (e.target.value && createForm.selectedAddress) {
                      setCreateForm(prev => ({ ...prev, selectedAddress: null }));
                    }
                  }}
                  placeholder="Enter address"
                  isDisabled={!!createForm.selectedAddress}
                />
                {createForm.selectedAddress && (
                  <Text fontSize="xs" color="gray.600" mt={1}>
                    Using saved address. Select a different address from the dropdown above or clear customer selection to enter manually.
                  </Text>
                )}
              </FormControl>
              <FormControl>
                <FormLabel>Technician</FormLabel>
                <Select
                  value={createForm.technicianId}
                  onChange={(e) => handleCreateFormChange('technicianId', e.target.value)}
                >
                  <option value="">Unassigned</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.firstName} {tech.lastName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <Checkbox
                  isChecked={createForm.isPoolBooking}
                  onChange={(e) => handleCreateFormChange('isPoolBooking', e.target.checked.toString())}
                >
                  Pool Booking
                </Checkbox>
              </FormControl>
              {createForm.isPoolBooking && (
                <FormControl>
                  <FormLabel>Select Pool</FormLabel>
                  <Select
                    value={createForm.poolId}
                    onChange={(e) => handleCreateFormChange('poolId', e.target.value)}
                  >
                    <option value="">Select a pool</option>
                    {pools.filter(p => p.status === 'forming' || p.status === 'confirmed').map(pool => (
                      <option key={pool.id} value={pool.id}>
                        {pool.name}
                      </option>
                    ))}
                  </Select>
                </FormControl>
              )}
              <FormControl>
                <FormLabel>Notes</FormLabel>
                <Textarea
                  value={createForm.notes}
                  onChange={(e) => handleCreateFormChange('notes', e.target.value)}
                  placeholder="Enter notes"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsCreateOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="blue" onClick={handleCreateBooking} isLoading={creating}>
              Create
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Create Pool Modal */}
      <Modal isOpen={isCreatePoolOpen} onClose={() => setIsCreatePoolOpen(false)} size={{ base: 'full', md: 'xl' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create New Booking Pool</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Pool Name</FormLabel>
                <Input
                  value={createPoolForm.name}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, name: e.target.value }))}
                  placeholder="Enter pool name"
                />
              </FormControl>
              <FormControl>
                <FormLabel>Description</FormLabel>
                <Textarea
                  value={createPoolForm.description}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, description: e.target.value }))}
                  placeholder="Enter pool description"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Label</FormLabel>
                <Input
                  value={createPoolForm.label}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, label: e.target.value }))}
                  placeholder="Enter pool label"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Street</FormLabel>
                <Input
                  value={createPoolForm.street}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, street: e.target.value }))}
                  placeholder="Enter street"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>City</FormLabel>
                <Input
                  value={createPoolForm.city}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, city: e.target.value }))}
                  placeholder="Enter city"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>State</FormLabel>
                <Input
                  value={createPoolForm.state}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, state: e.target.value }))}
                  placeholder="Enter state"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Zip Code</FormLabel>
                <Input
                  value={createPoolForm.zipCode}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, zipCode: e.target.value }))}
                  placeholder="Enter zip code"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Latitude</FormLabel>
                <Input
                  type="number"
                  value={createPoolForm.latitude}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, latitude: e.target.value }))}
                  placeholder="Enter latitude"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Longitude</FormLabel>
                <Input
                  type="number"
                  value={createPoolForm.longitude}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, longitude: e.target.value }))}
                  placeholder="Enter longitude"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Start Time</FormLabel>
                <Input
                  type="datetime-local"
                  value={createPoolForm.startTime}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, startTime: e.target.value }))}
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Input
                  value={createPoolForm.type}
                  onChange={(e) => setCreatePoolForm(prev => ({ ...prev, type: e.target.value }))}
                  placeholder="Enter pool type"
                />
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={() => setIsCreatePoolOpen(false)}>
              Cancel
            </Button>
            <Button colorScheme="purple" onClick={handleCreatePool} isLoading={creatingPool}>
              Create Pool
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Pool Details Drawer */}
      <Drawer
        isOpen={isPoolDrawerOpen}
        placement="right"
        onClose={() => setIsPoolDrawerOpen(false)}
        size="lg"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>Pool Details</DrawerHeader>
          <DrawerBody>
            {selectedPool && (
              <VStack spacing={6} align="stretch">
                <Card>
                  <CardHeader>
                    <Heading size="md">{selectedPool.name}</Heading>
                    <Badge colorScheme={getPoolStatusColor(selectedPool.status)}>
                      {selectedPool.status}
                    </Badge>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <Text>{selectedPool.label}</Text>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Address:</Text>
                        <Text fontSize="sm">
                          {(selectedPool.street || (selectedPool.poolAddress && typeof selectedPool.poolAddress === 'object' ? selectedPool.poolAddress.street : '') || 'N/A')}, {(selectedPool.city || (selectedPool.poolAddress && typeof selectedPool.poolAddress === 'object' ? selectedPool.poolAddress.city : '') || 'N/A')}, {(selectedPool.state || (selectedPool.poolAddress && typeof selectedPool.poolAddress === 'object' ? selectedPool.poolAddress.state : '') || 'N/A')} {(selectedPool.zipCode || (selectedPool.poolAddress && typeof selectedPool.poolAddress === 'object' ? selectedPool.poolAddress.zipCode : '') || 'N/A')}
                        </Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Type:</Text>
                        <Text fontSize="sm">{selectedPool.type}</Text>
                      </HStack>
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Status:</Text>
                        <Text fontSize="sm">{selectedPool.status}</Text>
                      </HStack>
                      <Progress 
                        value={(poolBookings.length / (poolBookings.length || 1)) * 100} 
                        colorScheme="purple" 
                      />
                      <HStack justify="space-between">
                        <Text fontWeight="bold">Revenue:</Text>
                        <Text fontWeight="bold">{formatAmount(poolRevenue)}</Text>
                      </HStack>
                    </VStack>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <Heading size="md">Participants</Heading>
                  </CardHeader>
                  <CardBody>
                    <List spacing={3}>
                      {poolParticipants.map((participant, index) => (
                        <ListItem key={index}>
                          <HStack>
                            <ListIcon as={FiUserIcon} color="green.500" />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold">{participant.customerName}</Text>
                              <Text fontSize="sm" color="gray.600">{participant.email}</Text>
                              <Text fontSize="xs" color="gray.500">
                                Joined: {formatDate(participant.joinedAt)}
                              </Text>
                            </VStack>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </CardBody>
                </Card>

                <Card>
                  <CardHeader>
                    <Heading size="md">Bookings</Heading>
                  </CardHeader>
                  <CardBody>
                    <List spacing={3}>
                      {poolBookings.map((booking, index) => (
                        <ListItem key={index}>
                          <HStack>
                            <ListIcon as={FiUserIcon} color="purple.500" />
                            <VStack align="start" spacing={0}>
                              <Text fontWeight="bold">{booking.customerName}</Text>
                              {(() => {
                                const serviceDetails = getServiceDetails(booking);
                                return (
                                  <>
                                    <Text fontSize="sm" color="gray.600">{serviceDetails.displayName}</Text>
                                    {serviceDetails.addOnNames.length > 0 && (
                                      <Text fontSize="xs" color="gray.500">
                                        Add-ons: {serviceDetails.addOnNames.join(', ')}
                                      </Text>
                                    )}
                                  </>
                                );
                              })()}
                              <Text fontSize="xs" color="gray.500">
                                Date: {formatDate(booking.scheduledDate)}
                              </Text>
                            </VStack>
                          </HStack>
                        </ListItem>
                      ))}
                    </List>
                  </CardBody>
                </Card>
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Booking Details Drawer */}
      <Drawer
        isOpen={isBookingDrawerOpen}
        placement="right"
        onClose={() => setIsBookingDrawerOpen(false)}
        size="xl"
      >
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader>
            <Flex justify="space-between" align="center">
              <Text>Booking Details</Text>
              <Button
                size="sm"
                colorScheme={isEditingBooking ? "gray" : "blue"}
                onClick={() => setIsEditingBooking(!isEditingBooking)}
              >
                {isEditingBooking ? "Cancel Edit" : "Edit Booking"}
              </Button>
            </Flex>
          </DrawerHeader>
          <DrawerBody>
            {viewingBooking && (
              <VStack spacing={6} align="stretch">
                <Card>
                  <CardHeader>
                    <Heading size="md">Booking Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Customer Name</FormLabel>
                        {isEditingBooking ? (
                          <Input
                            value={bookingEditForm.customerName}
                            onChange={(e) => setBookingEditForm(prev => ({ ...prev, customerName: e.target.value }))}
                          />
                        ) : (
                          <Text>{getCustomerName(viewingBooking)}</Text>
                        )}
                      </FormControl>

                      <FormControl>
                        <FormLabel>Service</FormLabel>
                        {isEditingBooking ? (
                          <VStack align="stretch" spacing={3}>
                            <Select
                              value={bookingEditForm.serviceType}
                              onChange={(e) => {
                                setBookingEditForm(prev => ({ 
                                  ...prev, 
                                  serviceType: e.target.value,
                                  variantId: '', // Reset variant when service changes
                                  addOns: [] // Reset add-ons when service changes
                                }));
                              }}
                            >
                              <option value="">Select service type</option>
                              {serviceTypes.map(st => (
                                <option key={st.id} value={st.id}>{st.name}</option>
                              ))}
                            </Select>
                            
                            {/* Variant Selection */}
                            {(() => {
                              const selectedServiceType = serviceTypes.find(st => st.id === bookingEditForm.serviceType);
                              if (selectedServiceType?.variants && selectedServiceType.variants.length > 0) {
                                const activeVariants = selectedServiceType.variants.filter(v => v.isActive !== false);
                                if (activeVariants.length > 0) {
                                  return (
                                    <Select
                                      value={bookingEditForm.variantId}
                                      onChange={(e) => setBookingEditForm(prev => ({ ...prev, variantId: e.target.value }))}
                                      placeholder="Select variant (optional)"
                                    >
                                      <option value="">No variant</option>
                                      {activeVariants.map(variant => (
                                        <option key={variant.id} value={variant.id}>
                                          {variant.name} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ''}
                                        </option>
                                      ))}
                                    </Select>
                                  );
                                }
                              }
                              return null;
                            })()}

                            {/* Add-ons Selection */}
                            {(() => {
                              const selectedServiceType = serviceTypes.find(st => st.id === bookingEditForm.serviceType);
                              if (selectedServiceType?.extras && selectedServiceType.extras.length > 0) {
                                const activeExtras = selectedServiceType.extras.filter(e => e.isActive !== false);
                                if (activeExtras.length > 0) {
                                  return (
                                    <Box p={3} borderWidth="1px" borderRadius="md" borderColor="gray.200">
                                      <Text fontSize="sm" fontWeight="semibold" mb={2}>Add-ons:</Text>
                                      <VStack align="start" spacing={2}>
                                        {activeExtras.map(extra => (
                                          <Checkbox
                                            key={extra.id}
                                            isChecked={bookingEditForm.addOns.includes(extra.id)}
                                            onChange={(e) => {
                                              const isChecked = e.target.checked;
                                              setBookingEditForm(prev => ({
                                                ...prev,
                                                addOns: isChecked
                                                  ? [...prev.addOns, extra.id]
                                                  : prev.addOns.filter(id => id !== extra.id)
                                              }));
                                            }}
                                          >
                                            {extra.name} {extra.price > 0 ? `(+$${extra.price.toFixed(2)})` : ''}
                                          </Checkbox>
                                        ))}
                                      </VStack>
                                    </Box>
                                  );
                                }
                              }
                              return null;
                            })()}
                          </VStack>
                        ) : (
                          (() => {
                            const serviceDetails = getServiceDetails(viewingBooking);
                            return (
                              <VStack align="start" spacing={1}>
                                <Text>{serviceDetails.displayName}</Text>
                                {serviceDetails.addOnNames.length > 0 && (
                                  <Box>
                                    <Text fontSize="sm" fontWeight="semibold" color="gray.600" mb={1}>
                                      Add-ons:
                                    </Text>
                                    <List spacing={1}>
                                      {serviceDetails.addOnNames.map((addOnName: string, idx: number) => (
                                        <ListItem key={idx}>
                                          <Text fontSize="sm" color="gray.600">• {addOnName}</Text>
                                        </ListItem>
                                      ))}
                                    </List>
                                  </Box>
                                )}
                              </VStack>
                            );
                          })()
                        )}
                      </FormControl>

                      {/* Multi-Service Section (Edit Form) */}
                      {isEditingBooking && (
                        <Box p={4} borderWidth="1px" borderRadius="md" borderColor="blue.200" bg="blue.50">
                          <HStack justify="space-between" mb={3}>
                            <FormLabel mb={0} fontWeight="semibold">Services</FormLabel>
                            <Button
                              size="sm"
                              leftIcon={<FiPlusIcon />}
                              onClick={() => {
                                setBookingEditForm(prev => ({
                                  ...prev,
                                  services: [...prev.services, {
                                    serviceTypeId: '',
                                    variantId: '',
                                    addOns: [],
                                    vehicleCount: 1,
                                    quantity: 1,
                                  }]
                                }));
                              }}
                            >
                              Add Service
                            </Button>
                          </HStack>
                          {bookingEditForm.services.length === 0 ? (
                            <Text fontSize="sm" color="gray.600" fontStyle="italic">
                              No services added. Click "Add Service" to add multiple services, or use the single service fields above.
                            </Text>
                          ) : (
                            <VStack align="stretch" spacing={3}>
                              {bookingEditForm.services.map((service, index) => (
                                <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="white">
                                  <HStack justify="space-between" mb={2}>
                                    <Text fontWeight="semibold">Service {index + 1}</Text>
                                    <IconButton
                                      aria-label="Remove service"
                                      icon={<FiTrash2Icon />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => {
                                        setBookingEditForm(prev => ({
                                          ...prev,
                                          services: prev.services.filter((_, i) => i !== index)
                                        }));
                                      }}
                                    />
                                  </HStack>
                                  <VStack align="stretch" spacing={2}>
                                    <Select
                                      placeholder="Select service type"
                                      value={service.serviceTypeId}
                                      onChange={(e) => {
                                        const updatedServices = [...bookingEditForm.services];
                                        updatedServices[index] = {
                                          ...service,
                                          serviceTypeId: e.target.value,
                                          variantId: '',
                                          addOns: [],
                                        };
                                        setBookingEditForm(prev => ({ ...prev, services: updatedServices }));
                                      }}
                                    >
                                      <option value="">Select service type</option>
                                      {serviceTypes.map(st => (
                                        <option key={st.id} value={st.id}>{st.name}</option>
                                      ))}
                                    </Select>
                                    
                                    {/* Variant for this service */}
                                    {(() => {
                                      const selectedServiceType = serviceTypes.find(st => st.id === service.serviceTypeId);
                                      if (selectedServiceType?.variants && selectedServiceType.variants.length > 0) {
                                        const activeVariants = selectedServiceType.variants.filter(v => v.isActive !== false);
                                        if (activeVariants.length > 0) {
                                          return (
                                            <Select
                                              placeholder="Select variant (optional)"
                                              value={service.variantId}
                                              onChange={(e) => {
                                                const updatedServices = [...bookingEditForm.services];
                                                updatedServices[index] = { ...service, variantId: e.target.value };
                                                setBookingEditForm(prev => ({ ...prev, services: updatedServices }));
                                              }}
                                            >
                                              <option value="">No variant</option>
                                              {activeVariants.map(variant => (
                                                <option key={variant.id} value={variant.id}>
                                                  {variant.name} {variant.price > 0 ? `(+$${variant.price.toFixed(2)})` : ''}
                                                </option>
                                              ))}
                                            </Select>
                                          );
                                        }
                                      }
                                      return null;
                                    })()}

                                    {/* Add-ons for this service */}
                                    {(() => {
                                      const selectedServiceType = serviceTypes.find(st => st.id === service.serviceTypeId);
                                      if (selectedServiceType?.extras && selectedServiceType.extras.length > 0) {
                                        const activeExtras = selectedServiceType.extras.filter(e => e.isActive !== false);
                                        if (activeExtras.length > 0) {
                                          return (
                                            <Box>
                                              <Text fontSize="sm" fontWeight="semibold" mb={1}>Add-ons:</Text>
                                              <VStack align="start" spacing={1}>
                                                {activeExtras.map(extra => (
                                                  <Checkbox
                                                    key={extra.id}
                                                    size="sm"
                                                    isChecked={service.addOns.includes(extra.id)}
                                                    onChange={(e) => {
                                                      const isChecked = e.target.checked;
                                                      const updatedServices = [...bookingEditForm.services];
                                                      updatedServices[index] = {
                                                        ...service,
                                                        addOns: isChecked
                                                          ? [...service.addOns, extra.id]
                                                          : service.addOns.filter(id => id !== extra.id)
                                                      };
                                                      setBookingEditForm(prev => ({ ...prev, services: updatedServices }));
                                                    }}
                                                  >
                                                    {extra.name} {extra.price > 0 ? `(+$${extra.price.toFixed(2)})` : ''}
                                                  </Checkbox>
                                                ))}
                                              </VStack>
                                            </Box>
                                          );
                                        }
                                      }
                                      return null;
                                    })()}

                                    <HStack>
                                      <FormControl>
                                        <FormLabel fontSize="sm">Vehicle Count</FormLabel>
                                        <Input
                                          type="number"
                                          min="1"
                                          size="sm"
                                          value={service.vehicleCount}
                                          onChange={(e) => {
                                            const updatedServices = [...bookingEditForm.services];
                                            updatedServices[index] = {
                                              ...service,
                                              vehicleCount: parseInt(e.target.value) || 1,
                                            };
                                            setBookingEditForm(prev => ({ ...prev, services: updatedServices }));
                                          }}
                                        />
                                      </FormControl>
                                      <FormControl>
                                        <FormLabel fontSize="sm">Quantity</FormLabel>
                                        <Input
                                          type="number"
                                          min="1"
                                          size="sm"
                                          value={service.quantity}
                                          onChange={(e) => {
                                            const updatedServices = [...bookingEditForm.services];
                                            updatedServices[index] = {
                                              ...service,
                                              quantity: parseInt(e.target.value) || 1,
                                            };
                                            setBookingEditForm(prev => ({ ...prev, services: updatedServices }));
                                          }}
                                        />
                                      </FormControl>
                                    </HStack>
                                  </VStack>
                                </Box>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      )}

                      {/* Multi-Vehicle Section (Edit Form) */}
                      {isEditingBooking && (
                        <Box p={4} borderWidth="1px" borderRadius="md" borderColor="green.200" bg="green.50">
                          <HStack justify="space-between" mb={3}>
                            <FormLabel mb={0} fontWeight="semibold">Vehicles</FormLabel>
                            <Button
                              size="sm"
                              leftIcon={<FiPlusIcon />}
                              onClick={() => {
                                setBookingEditForm(prev => ({
                                  ...prev,
                                  vehicles: [...prev.vehicles, {
                                    vehicleInfo: {
                                      make: '',
                                      model: '',
                                      year: '',
                                      color: '',
                                      licensePlate: '',
                                    }
                                  }]
                                }));
                              }}
                            >
                              Add Vehicle
                            </Button>
                          </HStack>
                          {bookingEditForm.vehicles.length === 0 ? (
                            <Text fontSize="sm" color="gray.600" fontStyle="italic" mb={3}>
                              No vehicles added. Click "Add Vehicle" to add multiple vehicles, or use the single vehicle fields below.
                            </Text>
                          ) : (
                            <VStack align="stretch" spacing={3}>
                              {bookingEditForm.vehicles.map((vehicle, index) => (
                                <Box key={index} p={3} borderWidth="1px" borderRadius="md" bg="white">
                                  <HStack justify="space-between" mb={2}>
                                    <Text fontWeight="semibold">Vehicle {index + 1}</Text>
                                    <IconButton
                                      aria-label="Remove vehicle"
                                      icon={<FiTrash2Icon />}
                                      size="sm"
                                      colorScheme="red"
                                      variant="ghost"
                                      onClick={() => {
                                        setBookingEditForm(prev => ({
                                          ...prev,
                                          vehicles: prev.vehicles.filter((_, i) => i !== index)
                                        }));
                                      }}
                                    />
                                  </HStack>
                                  <SimpleGrid columns={2} spacing={2}>
                                    <FormControl>
                                      <FormLabel fontSize="sm">Make</FormLabel>
                                      <Input
                                        size="sm"
                                        value={vehicle.vehicleInfo.make}
                                        onChange={(e) => {
                                          const updatedVehicles = [...bookingEditForm.vehicles];
                                          updatedVehicles[index] = {
                                            ...vehicle,
                                            vehicleInfo: { ...vehicle.vehicleInfo, make: e.target.value }
                                          };
                                          setBookingEditForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel fontSize="sm">Model</FormLabel>
                                      <Input
                                        size="sm"
                                        value={vehicle.vehicleInfo.model}
                                        onChange={(e) => {
                                          const updatedVehicles = [...bookingEditForm.vehicles];
                                          updatedVehicles[index] = {
                                            ...vehicle,
                                            vehicleInfo: { ...vehicle.vehicleInfo, model: e.target.value }
                                          };
                                          setBookingEditForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel fontSize="sm">Year</FormLabel>
                                      <Input
                                        size="sm"
                                        type="number"
                                        value={vehicle.vehicleInfo.year}
                                        onChange={(e) => {
                                          const updatedVehicles = [...bookingEditForm.vehicles];
                                          updatedVehicles[index] = {
                                            ...vehicle,
                                            vehicleInfo: { ...vehicle.vehicleInfo, year: e.target.value }
                                          };
                                          setBookingEditForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl>
                                      <FormLabel fontSize="sm">Color</FormLabel>
                                      <Input
                                        size="sm"
                                        value={vehicle.vehicleInfo.color}
                                        onChange={(e) => {
                                          const updatedVehicles = [...bookingEditForm.vehicles];
                                          updatedVehicles[index] = {
                                            ...vehicle,
                                            vehicleInfo: { ...vehicle.vehicleInfo, color: e.target.value }
                                          };
                                          setBookingEditForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                                        }}
                                      />
                                    </FormControl>
                                    <FormControl gridColumn="span 2">
                                      <FormLabel fontSize="sm">License Plate</FormLabel>
                                      <Input
                                        size="sm"
                                        value={vehicle.vehicleInfo.licensePlate}
                                        onChange={(e) => {
                                          const updatedVehicles = [...bookingEditForm.vehicles];
                                          updatedVehicles[index] = {
                                            ...vehicle,
                                            vehicleInfo: { ...vehicle.vehicleInfo, licensePlate: e.target.value }
                                          };
                                          setBookingEditForm(prev => ({ ...prev, vehicles: updatedVehicles }));
                                        }}
                                      />
                                    </FormControl>
                                  </SimpleGrid>
                                </Box>
                              ))}
                            </VStack>
                          )}
                        </Box>
                      )}

                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>Date</FormLabel>
                          {isEditingBooking ? (
                            <Input
                              type="date"
                              value={bookingEditForm.scheduledDate}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, scheduledDate: e.target.value }))}
                            />
                          ) : (
                            <Text>{formatDate(viewingBooking.scheduledTime || viewingBooking.scheduledDate)}</Text>
                          )}
                        </FormControl>
                        <FormControl>
                          <FormLabel>Time</FormLabel>
                          {isEditingBooking ? (
                            <Input
                              type="time"
                              value={bookingEditForm.scheduledTime}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, scheduledTime: e.target.value }))}
                            />
                          ) : (
                            <Text>{formatDate(viewingBooking.scheduledTime || viewingBooking.scheduledDate)}</Text>
                          )}
                        </FormControl>
                      </HStack>

                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>Status</FormLabel>
                          {isEditingBooking ? (
                            <Select
                              value={bookingEditForm.status}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, status: e.target.value as 'pending' | 'confirmed' | 'in_progress' | 'completed' | 'cancelled' }))}
                            >
                              <option value="pending">Pending</option>
                              <option value="confirmed">Confirmed</option>
                              <option value="in_progress">In Progress</option>
                              <option value="completed">Completed</option>
                              <option value="cancelled">Cancelled</option>
                            </Select>
                          ) : (
                            <Badge colorScheme={getStatusColor(viewingBooking.status)}>
                              {viewingBooking.status}
                            </Badge>
                          )}
                        </FormControl>
                        <FormControl>
                          <FormLabel>Amount</FormLabel>
                          {isEditingBooking ? (
                            <Input
                              type="number"
                              step="0.01"
                              value={bookingEditForm.amount}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, amount: e.target.value }))}
                            />
                          ) : (
                            <Text>{formatAmount(viewingBooking.amount)}</Text>
                          )}
                        </FormControl>
                      </HStack>

                      <FormControl>
                        <FormLabel>Address</FormLabel>
                        {isEditingBooking ? (
                          <Input
                            value={bookingEditForm.address}
                            onChange={(e) => setBookingEditForm(prev => ({ ...prev, address: e.target.value }))}
                          />
                        ) : (
                          <Text>
                            {(() => {
                              if (typeof viewingBooking.address === 'object' && viewingBooking.address !== null) {
                                const addr = viewingBooking.address as any;
                                const street = addr.street || addr.line1 || '';
                                const city = addr.city || '';
                                const state = addr.state || '';
                                const zip = addr.zipCode || '';
                                return [street, city, state, zip].filter(Boolean).join(', ') || 'N/A';
                              } else if (typeof viewingBooking.address === 'string') {
                                return viewingBooking.address || 'N/A';
                              }
                              return 'N/A';
                            })()}
                          </Text>
                        )}
                      </FormControl>

                      <FormControl>
                        <FormLabel>Technician</FormLabel>
                        {isEditingBooking ? (
                          <Select
                            value={bookingEditForm.technicianId}
                            onChange={(e) => setBookingEditForm(prev => ({ ...prev, technicianId: e.target.value }))}
                          >
                            <option value="">Unassigned</option>
                            {technicians.map(tech => (
                              <option key={tech.id} value={tech.id}>
                                {tech.firstName} {tech.lastName}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <Text>{getTechnicianName(viewingBooking.technicianId || '')}</Text>
                        )}
                      </FormControl>

                      <FormControl>
                        <FormLabel>Pool Assignment</FormLabel>
                        {isEditingBooking ? (
                          <VStack align="stretch" spacing={2}>
                            <Checkbox
                              isChecked={bookingEditForm.isPoolBooking}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, isPoolBooking: e.target.checked }))}
                            >
                              Pool Booking
                            </Checkbox>
                            {bookingEditForm.isPoolBooking && (
                              <Select
                                value={bookingEditForm.poolId}
                                onChange={(e) => setBookingEditForm(prev => ({ ...prev, poolId: e.target.value }))}
                              >
                                <option value="">Select a pool</option>
                                {pools.map(pool => (
                                  <option key={pool.id} value={pool.id}>
                                    {pool.name}
                                  </option>
                                ))}
                              </Select>
                            )}
                          </VStack>
                        ) : (
                          <Text>
                            {viewingBooking.poolId ? (viewingBooking.poolName || 'Pool Booking') : 'Individual'}
                          </Text>
                        )}
                      </FormControl>

                      <FormControl>
                        <FormLabel>Notes</FormLabel>
                        {isEditingBooking ? (
                          <Textarea
                            value={bookingEditForm.notes}
                            onChange={(e) => setBookingEditForm(prev => ({ ...prev, notes: e.target.value }))}
                          />
                        ) : (
                          <Text>{viewingBooking.notes || 'N/A'}</Text>
                        )}
                      </FormControl>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Payment Information Card */}
                <Card>
                  <CardHeader>
                    <Heading size="md">Payment Information</Heading>
                  </CardHeader>
                  <CardBody>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel>Payment Status</FormLabel>
                        {isEditingBooking ? (
                          <Select
                            value={bookingEditForm.paymentStatus}
                            onChange={(e) => setBookingEditForm(prev => ({ ...prev, paymentStatus: e.target.value as '' | 'pending' | 'partial' | 'deposit_paid' | 'paid' | 'refunded' | 'failed' }))}
                          >
                            <option value="">Not Set</option>
                            <option value="pending">Pending</option>
                            <option value="partial">Partial</option>
                            <option value="deposit_paid">Deposit Paid</option>
                            <option value="paid">Paid</option>
                            <option value="refunded">Refunded</option>
                            <option value="failed">Failed</option>
                          </Select>
                        ) : (
                          <Badge colorScheme={viewingBooking.paymentStatus === 'paid' ? 'green' : viewingBooking.paymentStatus === 'failed' ? 'red' : 'yellow'}>
                            {viewingBooking.paymentStatus || 'Not Set'}
                          </Badge>
                        )}
                      </FormControl>

                      <HStack spacing={4}>
                        <FormControl>
                          <FormLabel>Amount Paid ($)</FormLabel>
                          {isEditingBooking ? (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={bookingEditForm.amountPaid}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, amountPaid: e.target.value }))}
                              placeholder="0.00"
                            />
                          ) : (
                            <Text>{viewingBooking.amountPaid ? formatAmount(viewingBooking.amountPaid) : 'N/A'}</Text>
                          )}
                        </FormControl>

                        <FormControl>
                          <FormLabel>Remaining Amount ($)</FormLabel>
                          {isEditingBooking ? (
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              value={bookingEditForm.remainingAmount}
                              onChange={(e) => setBookingEditForm(prev => ({ ...prev, remainingAmount: e.target.value }))}
                              placeholder="0.00"
                            />
                          ) : (
                            <Text>{viewingBooking.remainingAmount ? formatAmount(viewingBooking.remainingAmount) : 'N/A'}</Text>
                          )}
                        </FormControl>
                      </HStack>

                      {!isEditingBooking && (
                        <Alert status="info" size="sm">
                          <AlertIcon />
                          <AlertDescription fontSize="sm">
                            {viewingBooking.paymentStatus === 'paid' 
                              ? 'This booking has been fully paid.'
                              : viewingBooking.paymentStatus === 'partial' || viewingBooking.paymentStatus === 'deposit_paid'
                              ? 'This booking has a partial payment.'
                              : viewingBooking.paymentStatus === 'failed'
                              ? 'Payment failed for this booking.'
                              : 'Payment status not set. Use "Edit Booking" to update payment information for non-Stripe payments.'}
                          </AlertDescription>
                        </Alert>
                      )}
                    </VStack>
                  </CardBody>
                </Card>

                {/* Edit buttons */}
                {isEditingBooking && (
                  <HStack spacing={4} justify="flex-end" mt={4}>
                    <Button variant="ghost" onClick={handleCancelEdit}>
                      Cancel
                    </Button>
                    <Button
                      colorScheme="blue"
                      onClick={handleSaveBookingDetails}
                      isLoading={savingBooking}
                    >
                      Save Changes
                    </Button>
                  </HStack>
                )}
              </VStack>
            )}
          </DrawerBody>
        </DrawerContent>
      </Drawer>

      {/* Export Modal */}
      <ExportModal
        isOpen={isExportOpen}
        onClose={() => setIsExportOpen(false)}
        data={filteredBookings as any}
        title="Export Bookings"
        defaultFilename="bookings_export"
      />

      {/* Bulk Update Modal */}
      <Modal isOpen={isBulkOpen} onClose={onBulkClose} size={{ base: 'full', md: 'lg' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Bulk Update Bookings</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select value={bulkStatus} onChange={(e) => setBulkStatus(e.target.value)}>
                  <option value="">No change</option>
                  <option value="pending">Pending</option>
                  <option value="confirmed">Confirmed</option>
                  <option value="in_progress">In Progress</option>
                  <option value="completed">Completed</option>
                  <option value="cancelled">Cancelled</option>
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>Technician</FormLabel>
                <Select value={bulkTechnician} onChange={(e) => setBulkTechnician(e.target.value)}>
                  <option value="">No change</option>
                  {technicians.map(tech => (
                    <option key={tech.id} value={tech.id}>
                      {tech.firstName} {tech.lastName}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onBulkClose}>
              Cancel
            </Button>
            <Button colorScheme="orange" onClick={handleBulkUpdate} isLoading={bulkUpdating}>
              Update {selectedBookings.length} Bookings
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default BookingsManagement; 